import { cloneDeep, isEqual as deepEquality } from "lodash";

import AIScribeAction from "@/AIScribe/Api/AIScribeAction";
import { AIScribeLaunchButton } from "@/AIScribe/Components/AIScribeLaunchButton";
import { IAIScribeContainerModel } from "@/AIScribe/Models/IAIScribeContainerModel";
import { FacturationNetManager } from "@/Billing/Services/FacturationNet/FacturationNetManager";
import { Client } from "@/Common/Api/Client";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TimerComponentModel } from "@/Common/Components/Inputs/TimerComponent";
import { LinkingPopupService } from "@/Common/Components/Modals/LinkingPopup/LinkingPopupService";
import { Popup } from "@/Common/Components/Modals/Popup";
import { DocumentProgressBarPopup } from "@/Common/Components/ProgressBar/DocumentProgressBarPopup";
import { SearchBoxControl } from "@/Common/Components/Search/SearchBoxControl";
import { PrintAndSignaturePopup } from "@/Common/Components/Sign/PrintAndSignaturePopup";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { HighlightHelper } from "@/Common/Helpers/HighlightHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { Patient } from "@/Common/Models/Patient/Patient";
import { MedicalItemsTab, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import { HighlightService } from "@/Common/Models/Services/HighlightService";
import { UserSettingsConfig } from "@/Common/Models/Settings/UserSettingsConfig";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { TimeSpan } from "@/Common/Models/TimeSpan";
import { PopupModel } from "@/Common/Models/UI/PopupModel";
import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { Dispatcher } from "@/Common/Stores/Actions/ActionDispatcher";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { ExportableFileDispatcher } from "@/ExportableFile/Helpers/ExportableFileDispatcher";
import { ExportableFileGenerationIntent } from "@/ExportableFile/Models/enum/ExportableFileGenerationIntent";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFilePopupSettingModel } from "@/ExportableFile/Models/ExportableFilePopupSettingModel";
import { FileTypeEncounter } from "@/ExportableFile/Models/FileTypes/FileTypeEncounter";
import { CreateFlowsheetRequest } from "@/Flowsheets/Api/Contracts/CreateFlowsheet";
import { FlowsheetClient } from "@/Flowsheets/Api/FlowsheetClient";
import { ApproveEncounterFlowsheetConfirmationModal } from "@/Flowsheets/Components/ApproveEncounterFlowsheetConfirmationModal/ApproveEncounterFlowsheetConfirmationModal";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";
import { FlowsheetTemplate } from "@/Flowsheets/Models/FlowsheetTemplate";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { MedicalSummaryPanelAction } from "@/Patient/Actions/MedicalSummaryPanelAction";
import { AutoSaveEncounterResponse } from "@/Patient/Api/Dto/Responses/AutoSaveEncounterResponse";
import { FreeFormImage } from "@/Patient/Api/Dto/Responses/FreeFormImage";
import { EncounterUnapprouvability, NoteLockedReason } from "@/Patient/Api/Dto/Responses/MedicalNoteModel";
import { GrowthChartApiClient } from "@/Patient/Api/GrowthChartApiClient";
import { MedfarBilling as MyleComponentsMedfarBilling } from "@/Patient/Components/MedfarBilling/MedfarBilling";
import { MedfarBillingSpecialtyPopup } from "@/Patient/Components/MedfarBilling/MedfarBillingSpecialtyPopup";
import { SyncRvqImmunizationPopupProps } from "@/Patient/Components/MedicalItems/Immunization/SyncRvqImmunizationPopup";
import { MedicalNoteActionWarningPopup } from "@/Patient/Components/MedicalNote/MedicalNoteActionWarningPopup";
import { MedicalNoteAlreadySignedOffWarningPopup } from "@/Patient/Components/MedicalNote/MedicalNoteAlreadySignedOffWarningPopup";
import { MedicalNoteAutosaveAlreadSignedOffWarningPopup } from "@/Patient/Components/MedicalNote/MedicalNoteAutosaveAlreadSignedOffWarningPopup";
import { MedicalNoteDeleteReasonPopup } from "@/Patient/Components/MedicalNote/MedicalNoteDeleteReasonPopup";
import { MedicalNoteEditConsultationDetailsPopup } from "@/Patient/Components/MedicalNote/MedicalNoteEditConsultationDetailsPopup";
import { MedicalNoteErrorWarningPopup } from "@/Patient/Components/MedicalNote/MedicalNoteErrorWarningPopup";
import { MedicalNotePopup } from "@/Patient/Components/MedicalNote/MedicalNotePopup";
import { NewMedicalNoteNoSpecialityWarningPopup } from "@/Patient/Components/MedicalNote/NewMedicalNoteNoSpecialityWarningPopup";
import { NewMedicalNoteWarningPopup } from "@/Patient/Components/MedicalNote/NewMedicalNoteWarningPopup";
import { PrivateBillCreationWarningPopup } from "@/Patient/Components/MedicalNote/PrivateBillCreationWarningPopup";
import { PrivateBillPopup } from "@/Patient/Components/MedicalNote/PrivateBillPopup";
import { ResidentApprovalPopup } from "@/Patient/Components/MedicalNote/ResidentApprovalPopup";
import { ReusingPrivateBillPopup } from "@/Patient/Components/MedicalNote/ReusingPrivateBillPopup";
import { WorkSafeBcFormValidator } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcFormValidator";
import { WorkSafeBcPhysicianReportModel } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcPhysicianReportModel";
import { WorkSafeBcWorkerModel } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcWorkerModel";
import { GetWorkSafeBcPhysicianReportFormResponse } from "@/Patient/Components/MedicalNote/WorkSafeBc/ResponseModels/GetWorkSafeBcPhysicianReportFormResponse";
import { WorkSafeBcController } from "@/Patient/Components/MedicalNote/WorkSafeBc/WorkSafeBcController";
import { AlertPopup } from "@/Patient/Components/MedicalSummary/AlertPopup";
import { MedicalNoteHelper } from "@/Patient/Helpers/MedicalNoteHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { FormType } from "@/Patient/Models/Forms/FormTypeEnum";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { DisplayContextEnum } from "@/Patient/Models/MedicalNote/Enum/DisplayContextEnum";
import { GenerateFileContext } from "@/Patient/Models/MedicalNote/Enum/GenerateFileContext";
import { SignedAuthorizationDocumentType } from "@/Patient/Models/MedicalNote/Enum/SignedAuthorizationDocumentType";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { MedicalNoteActionType, MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import { IPatientMedicalNoteModel, toPortalPopupModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { sharedItemProviderFactory } from "@/Portal/Services/SharedItemProviderFactory";
import { BaseSendMedicalNoteItemToPortalPopupStrategy } from "@/Portal/Services/Strategies/BaseSendMedicalNoteItemToPortalPopupStrategy";
import { MedicalRequestPopupController } from "@/Portfolio/Components/MedicalRequestForm/MedicalRequestPopupController";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { VitalSignPercentileUtility } from "../../Helpers/VitalSignPercentileUtility";
import { EncountersSidePanel } from "../Common/EncountersSidePanel";
import { MedicalSummaryPanel } from "../MedicalSummaryPanel/MedicalSummaryPanel";
import { MedicalSummaryPanelContext } from "../MedicalSummaryPanel/MedicalSummaryPanelEnums";
import { MedicalNotePopupAction } from "./Actions/MedicalNotePopupAction";
import { MedicalNoteForm } from "./Forms/MedicalNoteForm";
import { MedicalSummarySection } from "./MedicalSummarySection/MedicalSummarySection";
import { VitalSignSummary } from "./MedicalSummarySection/VitalSignSummary";
import { PlanSection } from "./PlanSection/PlanSection";
import { ProgressNote } from "./ProgressNote/ProgressNote";
import { AssessmentSection } from "./AssessmentSection";
import { BillingClaimSection } from "./BillingClaimSection";
import { MedicalNoteCancelAutoApproveWarningPopup } from "./MedicalNoteCancelAutoApproveWarningPopup";
import { MedicalNoteHeader } from "./MedicalNoteHeader";
import { MedicalNoteSections } from "./MedicalNoteSections";
import { PrivateBillEditPopup } from "./PrivateBillEditPopup";

import "./MedicalNote.css";
import styles from "./MedicalNote.module.scss";
import secondScreenStyles from "./SecondScreenNote.module.css";

import img_loading from "@/Globals/_images/loading.gif";

interface IMedicalNoteProps {
    PatientId: Guid;
    PatientUIModel: IPatientUIModel;
    NoteModel: IPatientMedicalNoteModel;
    MedicalSummaryModel: IPatientMedicalSummaryModel;
    PreferredPharmacy: IPharmacyInfo;
    ClinicianModel: IPatientCliniciansModel;
    PatientModel: Patient;
    isReadOnly?: boolean;
    isHighlighting?: boolean;
    displayOnlyApprovedEncounters?: boolean;
    isMedicalSummaryCollapsed?: boolean;
    onEncounterClick?: (encounterId: string) => void;
    onEncounterMyNotesFilterChecked: (checked: boolean) => void;
    onEncounterUnapprovedNotesFilterChecked: (checked: boolean) => void;
    onEncounterClinicVisitNotesFilterChecked: (checked: boolean) => void;
    displayContext: DisplayContextEnum;
}

class MedicalNoteState {
    public formsPopupOpen: boolean;
    public formsPopupType: FormType;

    public consultationRequestPinValid: boolean;
    public consultationRequestFaxValid: boolean;
    public consultationRequestFaxPopupState: FaxAndSignatureClinicianSelectionState; // do not use for logic flow or rendering control , only to get faxing data (ie clinician name, number, etc)

    public requestPortalPinValid: boolean;

    public errorMessageAddNewLab: string;

    public genericFaxInfoPinValid: boolean;
    public genericFaxInfoFaxValid: boolean;
    public genericFaxInfoName: string;
    public genericFaxInfoFax: string;

    public popup: JSX.Element;
    public revokeSignaturePopupModel: PopupModel;
    public tmpEmployeeDurationEdit: boolean;
    public tmpEmployeeDurationEditValue: TimeSpan;

    public radPdfFormSelectionPopupOpen: boolean;
    public medicalNoteCancelAutoApproveWarningPopupOpen: boolean;
    public showCRDSSupervisorPopup: boolean;
    public showCRDSSupervisorPopupCallback: (supervisorContactId: string) => void;
    public crdsSelectedSupervisorContactId: string;
    public hiddenClinicalFormIds: string[] = [];

    public showConsultationPopupUpgrade: boolean;
    public selectedConsultationRequestId: string;
    public selectedConsultationContextLookupId: string;

    public showLabRequestPopupUpgrade: boolean;
    public selectedLabRequestId: string;
    public selectedLabRequestType: LabRequestType;
    public willPrintAfterFax: boolean;

    public immunizationSyncPopupProps: SyncRvqImmunizationPopupProps | null;
    public immunizations: MedicalItemImmunization[];

    public showWorkSafeBcInvalidFieldsPopup: boolean;

    public currentVitalSign: MedicalItemVitalSign;

    public percentiles: { [index in VitalProperty]?: string };

    public showMedicalSummaryPanel: boolean;
    public allElementsToHighlight: DOMRect[];

    public isApproveFlowsheetsWarningVisible: boolean;

    public medicalNoteSections: MedicalNoteSections;

    public numberOfDocumentsToGenerate: number | null = null;
    public numberOfDocumentsGenerated: number | null = null;

    public aiScribeModel?: IAIScribeContainerModel;

    public medicalNoteCancelAutoApproveWarningPopupCallback: () => void = null;

    public static SetDefaultFaxData(s: MedicalNoteState): void {
        s.genericFaxInfoPinValid = false;
        s.genericFaxInfoFaxValid = false;
        s.genericFaxInfoName = "";
        s.genericFaxInfoFax = "";
    }
}

class MedicalNote extends BaseReactComponent<IMedicalNoteProps, MedicalNoteState> {
    private workSafeBcController = React.createRef<WorkSafeBcController>();
    private vitalSignPercentileUtility: VitalSignPercentileUtility = null;

    private houseVisitLookupId = cacheStore.FindLookupByLookupCode("House visit", "CONSULTATIONCONTEXT");
    private clinicalVisitLookupId = cacheStore.FindLookupByLookupCode("Clinic Visite", "CONSULTATIONCONTEXT");
    private webcamConsultationLookupId = cacheStore.FindLookupByLookupCode("Webcam", "CONSULTATIONCONTEXT");
    private summaryUpdateLookupId = cacheStore.FindLookupByLookupCode("Summary Update", "CONSULTATIONCONTEXT");
    private prescriptionLookupId = cacheStore.FindLookupByLookupCode("Prescription", "CONSULTATIONCONTEXT");

    private userSettings = new UserSettings({
        UserSettingsConfig: new UserSettingsConfig(cacheStore.UserSettings.UserSettingsConfig)
    });

    private handNoteWindow: Window;
    private handNoteWindowDispatcher: Dispatcher<any>;
    private miscDspReference: string;
    private autosaveIntervalInMs = 8000;
    private autosaveIntervalReference = null;
    private lastSucessfullEncounterRequest = null;
    private lastScrollPosition: number;
    private get currentEncounter(): Encounter { return this.props.NoteModel.get("Encounter"); }
    private medicalNoteScrollerDivElement: HTMLDivElement = null;

    private medicalNotePopupAction = new MedicalNotePopupAction();

    private highlightService: HighlightService = null;
    private medicalNoteContainerClassName: string = "medical-note-container-to-highlight";

    private shouldAutosave = false;
    private aiScribeListenerReference: string;

    public componentWillMount(): void {
        this.handNoteWindow = null;
        this.scrollDebounce = -1;
        this.lastScrollPosition = 0;

        const s = new MedicalNoteState();

        s.formsPopupOpen = false;
        s.radPdfFormSelectionPopupOpen = false;
        s.medicalNoteCancelAutoApproveWarningPopupOpen = false;

        s.consultationRequestPinValid = false;
        s.consultationRequestFaxValid = false;
        s.consultationRequestFaxPopupState = null;

        s.requestPortalPinValid = false;

        s.genericFaxInfoPinValid = false;
        s.genericFaxInfoFaxValid = false;
        s.genericFaxInfoFax = "";
        s.genericFaxInfoName = "";

        s.selectedLabRequestType = LabRequestType.None;
        s.willPrintAfterFax = false;

        s.errorMessageAddNewLab = "";

        s.immunizationSyncPopupProps = null;
        s.immunizations = this.props.MedicalSummaryModel.get("MedicalItemsImmunization").toArray();

        s.showMedicalSummaryPanel = !!cacheStore?.UserSettings.MedicalNoteMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen ?? true;
        s.allElementsToHighlight = [];
        s.medicalNoteSections = new MedicalNoteSections(this.props.NoteModel);
        s.aiScribeModel = aiScribeStore.Model;

        this.setState(s);
    }

    public componentDidMount(): void {
        this.miscDspReference = MiscDispatcher.register(event => this.miscDispatcherHandler(event));
        this.aiScribeListenerReference = aiScribeStore.AddChangeListener(this.updateAIScribeModel, this, "ai-scribe-in-patient-tab");

        let scrollTo = this.props.NoteModel.get("ScrollPosition");

        if (scrollTo > 0) {
            this.lastScrollPosition = scrollTo;
        }

        let elem = $(document).find("div[data-scrolling]");

        elem.scroll(this.reportScroll.bind(this, elem));
        $(window).trigger("resize");
        window.setTimeout(() => {
            elem.scrollTop(scrollTo);

            if (this.props?.isHighlighting && !this.highlightService) {
                this.initializeHighlightService();
                this.executeHighlightService(this.props.MedicalSummaryModel?.get("EncounterSearchTerm"), true);
            }
        }, 200);

        this.ResumeTimer();
        this.loadGrowthCharts();
        this.mutateState(s => {
            s.currentVitalSign = this.getCurrentVitalSign(this.props.NoteModel);
            if (this.props.isMedicalSummaryCollapsed) {
                s.medicalNoteSections.isMedicalSummarySectionExpanded = !s.medicalNoteSections.isMedicalSummarySectionExpanded;
            }
        }, () => this.calculateAllPercentiles(this.state.currentVitalSign));
    }

    private updateAIScribeModel(): void {
        this.mutateState(s => s.aiScribeModel = aiScribeStore.Model);
    }

    // some dependencies on scroll
    private scrollDebounce: number;
    private reportScroll(sender: JQuery): void {
        if (this.props.isReadOnly)
            return;

        let val = sender.scrollTop();
        this.lastScrollPosition = val;

        this.scrollDebounce;
        if (this.scrollDebounce !== -1) {
            clearTimeout(this.scrollDebounce);
            this.scrollDebounce = -1;
        }
        this.scrollDebounce = window.setTimeout(() => {
            let op = new LocalEvent(LocalPatientOperations.MedicalNote.SaveScrollPosition);
            op.AddItem("value", val);
            LocalEventDispatcher.dispatch(op);
        }, 300);
    }

    public componentDidUpdate(prevProps: IMedicalNoteProps): void {
        var self = this;

        if (self.props.NoteModel.get("ViewMode") == MedicalNoteViewMode.Edit && self.autosaveIntervalReference == null) {
            var ref = window.setInterval(() => {
                self.AutoSaveTextFields();
            }, self.autosaveIntervalInMs);
            self.autosaveIntervalReference = ref;
        }
        else if (self.props.NoteModel.get("ViewMode") == MedicalNoteViewMode.View && self.autosaveIntervalReference != null) {
            clearInterval(self.autosaveIntervalReference);
            self.autosaveIntervalReference = null;
        }

        $(window).trigger("resize");

        // this reference may be null as the related render method is currently into a try/catch
        if (this.medicalNoteScrollerDivElement)
            this.medicalNoteScrollerDivElement.scrollTop = this.lastScrollPosition;

        this.ResumeTimer();

        if (this.props?.isHighlighting) {
            if (prevProps.PatientId != this.props.PatientId) {
                this.terminateHighlightService();
                this.initializeHighlightService();
                this.executeHighlightService(this.props.MedicalSummaryModel?.get("EncounterSearchTerm"), true);
            }
            else if (
                prevProps.MedicalSummaryModel.get("EncounterSearchTerm") != this.props.MedicalSummaryModel.get("EncounterSearchTerm") ||
                prevProps.NoteModel.get("Encounter")?.Id !== this.props.NoteModel.get("Encounter")?.Id
            ) {
                this.executeHighlightService(this.props.MedicalSummaryModel?.get("EncounterSearchTerm"), true);
            }

            if (
                this.highlightService?.allElementsToHighlight &&
                this.state.allElementsToHighlight &&
                CommonHelper.isDOMRectArraysNotEqual(this.highlightService?.allElementsToHighlight, this.state.allElementsToHighlight)
            ) {
                this.executeHighlightService(this.props.MedicalSummaryModel?.get("EncounterSearchTerm"), false);
            }
        }
    }

    public componentWillReceiveProps(nextProps: IMedicalNoteProps): void {
        if (nextProps != this.props) {
            this.mutateState(s => s.immunizations = nextProps.MedicalSummaryModel.get("MedicalItemsImmunization").toArray());
        }
    }

    public componentWillUpdate(nextProps: IMedicalNoteProps): void {
        this.lastScrollPosition = this.medicalNoteScrollerDivElement?.scrollTop;

        // ensure clickable area textboxes are cleared before rendering other patient info
        if (nextProps.PatientId !== this.props.PatientId) {
            $(`#note_reason_${this.currentEncounter.Id}`).val("");
            $(`#note_exam_${this.currentEncounter.Id}`).val("");
            $(`#note_history_${this.currentEncounter.Id}`).val("");
            $(`#note_lab_${this.currentEncounter.Id}`).val("");
            $(`#note_imaging_${this.currentEncounter.Id}`).val("");
            $(`#note_other_${this.currentEncounter.Id}`).val("");
            $(`#note_otherplan_${this.currentEncounter.Id}`).val("");
        }
    }

    public componentWillUnmount(): void {
        let elem = $(document).find("div[data-scrolling]");
        elem.off("scroll");

        this.miscDspReference && MiscDispatcher.unregister(this.miscDspReference);
        aiScribeStore.RemoveChangeListener(this.aiScribeListenerReference);

        if (this.autosaveIntervalReference != null) {
            clearInterval(this.autosaveIntervalReference);
            this.autosaveIntervalReference = null;
        }

        if (this.props?.isHighlighting && this.highlightService) {
            this.highlightService.terminate();
        }
    }

    public shouldComponentUpdate(nextProps: IMedicalNoteProps, nextState: MedicalNoteState): boolean {
        return (
            this.props !== nextProps
            || this.state != nextState
            || this.state.genericFaxInfoFaxValid != nextState.genericFaxInfoFaxValid
        );
    }

    public render(): JSX.Element {
        const self = this;
        const noteModel = this.props.NoteModel;
        const summaryModel = this.props.MedicalSummaryModel;
        const leftMenuControlsCss = this.props.NoteModel.get("LeftMenuOpen") ? "aside-handle responsive" : "aside-handle";

        const shouldShowAlertPopup = summaryModel.get("ShowAlertPopup");
        const showProgressPopup = this.props.PatientUIModel.get("IsNoteApprovalInProgress");
        const formsBeingSentOnNoteApproval = this.props.PatientUIModel.get("FormsBeingSentOnNoteApproval");

        const shouldShowDeleteWarningPopup = noteModel.get("ShowDeleteNoteWarningPopup");
        const shouldShowUnapproveWarningPopup = noteModel.get("ShowUnapproveNoteWarningPopup");
        const shouldShowMedicalNoteErrorWarningPopup = noteModel.get("MedicalNoteErrorWarningPopup");

        const signedOffDate = this.currentEncounter.SignedOffDate;
        const signedOffByEmployeeId = this.currentEncounter.SignedOffByEmployeeId;

        const encounterSiteId = this.currentEncounter.SiteId;
        const currentEmployeeMedicalNoteAllowlist = cacheStore.GetCurrentEmployee().MedicalNoteAllowlist;

        return (
            <div
                className="medical-summary-container"
                onClick={() => this.closeClickableText()}
                data-cy="medical-note-container"
            >
                <div className="tb medical-summary">
                    {this.props.NoteModel.get("ShowNewMedfarBillPopup") === true && (
                        <Popup
                            altView
                            draggable
                            dragClass="fmp-popup-header"
                            cssClass="fmb-billing"
                            centeredHorizontally={true}
                            topPosition={10}
                        >
                            <MyleComponentsMedfarBilling
                                closeTarget={LocalPatientOperations.MedfarBilling.ClosePopup}
                                Model={this.props.NoteModel.get("MedfarBillModel")}
                                initialState={this.props.NoteModel.get("MedfarBillModelInitialState")}
                                IsSpecialist={this.props.NoteModel.get("IsMedfarBillingSpecialist")}
                                isPatientSubcribed={false}
                            />
                        </Popup>
                    )}

                    {shouldShowAlertPopup &&
                        <AlertPopup Model={summaryModel} />
                    }

                    {shouldShowDeleteWarningPopup &&
                        <MedicalNoteDeleteReasonPopup noteModel={noteModel} medicalSummaryModel={summaryModel} />
                    }

                    {shouldShowUnapproveWarningPopup &&
                        <MedicalNoteActionWarningPopup noteModel={noteModel} medicalSummaryModel={summaryModel} warningType={MedicalNoteActionType.Unapprove} />
                    }

                    {shouldShowMedicalNoteErrorWarningPopup &&
                        <MedicalNoteErrorWarningPopup noteModel={noteModel} />
                    }

                    {!this.isEditMode
                        && !this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup
                        && this.currentEncounter.AutoApprove
                        && this.state.medicalNoteCancelAutoApproveWarningPopupOpen
                        && (
                            <MedicalNoteCancelAutoApproveWarningPopup
                                onClose={() => this.mutateState(s => s.medicalNoteCancelAutoApproveWarningPopupOpen = false)}
                                onContinue={hideAutoApproveWarningPopup => this.onMedicalNoteCancelAutoApproveWarningPopupContinue(hideAutoApproveWarningPopup)}
                            />
                        )
                    }

                    {showProgressPopup && (
                        <Popup
                            Id="ProgressPopup"
                            title={cacheStore.GetString("InProgressLabel")}
                            topPosition={50}
                            centeredHorizontally
                            draggable={false}
                            manageOverlay
                            isOverlayTransparent={false}
                        >
                            <div className={styles.formsProgressPopupContent}>
                                <p>{cacheStore.GetString("NoteApproval_SendingForms")}</p>
                                <ul>
                                    {formsBeingSentOnNoteApproval.map(form => (
                                        <li key={form.key}>{form.value}</li>
                                    ))}
                                </ul>
                                <img className={styles.loadingSpinner} src={img_loading} alt="loading" />
                            </div>
                        </Popup>
                    )}

                    {this.state.numberOfDocumentsToGenerate > 0 &&
                        <DocumentProgressBarPopup currentValue={this.state.numberOfDocumentsGenerated} maxValue={this.state.numberOfDocumentsToGenerate} />
                    }
                    {this.props.NoteModel.get("LeftMenuOpen") === true && !this.props.isReadOnly && (
                        <EncountersSidePanel
                            model={this.props.MedicalSummaryModel}
                            noteModel={this.props.NoteModel}
                            isReadOnly={this.props.isReadOnly}
                            displayOnlyApprovedEncounters={this.props.displayOnlyApprovedEncounters}
                            encounterSearchTermChangedOperation={LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged}
                            fromMyleOperation={LocalPatientOperations.MedicalSummary.EncounterDateFromChanged}
                            toMyleOperation={LocalPatientOperations.MedicalSummary.EncounterDateToChanged}
                            fromChange={(date: Date) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterDateFromChanged, { "value": date })}
                            toChange={(date: Date) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterDateToChanged, { "value": date })}
                            onEncounterClick={this.props.onEncounterClick ?? this.onEncounterClick}
                            newNote={(e: React.MouseEvent) => this.newNote(e)}
                            showCriteria={() => this.showCriteria()}
                            toggleDisplayOnlyUnsigned={() => this.ToggleDisplayOnlyUnsigned()}
                            onMyNotesFilterChanged={checked => this.props.onEncounterMyNotesFilterChecked(checked)}
                            onClinicVisitNotesFilterChanged={checked => this.props.onEncounterClinicVisitNotesFilterChecked(checked)}
                            onEncounterSearchTermChanged={(textOrDate: any) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged, { "value": textOrDate })}
                        />
                    )}

                    <div onClick={() => this.toggleLeftMenu()} className={`${leftMenuControlsCss} ${this.props.isReadOnly ? secondScreenStyles.fullHeight : ""}`} data-cy="medicalNote-leftMenu-handle">
                        {this.props.NoteModel.get("LeftMenuOpen") === true && (
                            <div className="slide-menu-icon blue d">
                                <div className={this.props.isReadOnly ? secondScreenStyles.slideMenu : ""}>
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        )}
                        {this.props.NoteModel.get("LeftMenuOpen") === false && (
                            <div className="slide-menu-icon blue d">
                                <div className={this.props.isReadOnly ? secondScreenStyles.slideMenu : ""}>
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        )}
                    </div>
                    {this.RenderMedicalNoteColumn()}
                </div>

                {noteModel.get("IsEditConsultationDetailsPopupVisible") === true &&
                    <MedicalNoteEditConsultationDetailsPopup noteModel={this.props.NoteModel} patientContactId={this.props.PatientId} />
                }

                {noteModel.get("IsNotePopupOpen") === true && (
                    <MedicalNotePopup
                        ControlId="medicalnote_dropdown_main"
                        EventToDispatchOnSave={LocalPatientOperations.MedicalNote.AddEncounterNote}
                        EncounterId={noteModel.get("Encounter").Id}
                        NoteContext={noteModel.get("NotePopupContext")}
                        Notes={noteModel.get("EncounterNotes")}
                        leftPosition={noteModel.get("NotePopupLeft")}
                        topPosition={noteModel.get("NotePopupTop")}
                    />
                )}

                {noteModel.get("AttachedCommunicationsPopupOpen") === true && (
                    <AttachedCommunicationsPopup
                        controlId="attached_communications_encounter"
                        attachedCommunications={noteModel.get("AttachedCommunicationsPopupList")}
                        leftPosition={noteModel.get("AttachedCommunicationsPopupX")}
                        topPosition={noteModel.get("AttachedCommunicationsPopupY")}
                        context={noteModel.get("AttachedCommunicationsContext")}
                        handleRedirect={(ac, context) => this.redirectFromAttachedCommunicationsPopup(ac, context)}
                        handleClose={() => this.closeAttachedCommunicationsPopup()}
                        dataCy="medicalNote-attachedCommunication"
                    />
                )}

                {noteModel.get("IsRequestPortalPopupOpen") &&
                    this.getPortalPopupRender()
                }

                {this.state.showLabRequestPopupUpgrade && (
                    <MedicalRequestPopupController
                        type={MedicalRequestType.LabRequest}
                        id={this.state.selectedLabRequestId}
                        encounter={{
                            id: noteModel.get("Encounter").Id,
                            consultationDate: noteModel.get("Encounter").ConsultationDate,
                            isSignedOff: noteModel.get("Encounter").IsSignedOff,
                            SignedByEmployeeId: signedOffByEmployeeId,
                            SignedOffDate: signedOffDate
                        }}
                        siteId={encounterSiteId}
                        labRequestType={this.state.selectedLabRequestType}
                        patientDoB={this.props.PatientModel.IndividualResponse.DateOfBirth}
                        closeAfterButtonClick={true}
                        onClose={() => this.closeLabRequestPopupUpgrade()}
                        onSaveSuccess={(model, shouldSaveRelatedEmployee, printInfo) => {
                            let item = RequestsInboxItem.fromLabRequest(model as LabRequest, printInfo);
                            sessionStore.dispatch(LocalPatientOperations.MedicalNote.LabRequestPopup_OnSaveSucces, {
                                item: item,
                                patientContactId: this.props.NoteModel.get("PatientContactId"),
                                shouldSaveRelatedEmployee: shouldSaveRelatedEmployee
                            });
                        }}

                        onDeleteSuccess={(requestId, encounterId) => patientStore.onRequestDeletedSuccess(requestId, encounterId)}
                        onPrintSuccess={(fileName, requestId) => this.onMedicalRequestPrintSuccess(fileName, requestId, MedicalRequestType.LabRequest)}
                        onSendCallback={(model, sendStatus) => {
                            let patientContactId = this.props.NoteModel.get("PatientContactId");
                            let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];
                            let requests = patientNoteModel.get("Requests")
                                .toArray()
                                .mutate(obj => obj.SendStatus = sendStatus, obj => obj.RequestId == model.id)
                                .toImmutable();
                            patientNoteModel = patientNoteModel.set("Requests", requests);
                            patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
                            patientStore.EmitChangeToListeners();
                        }}
                        isPatientRegisteredToPortal={this.props.PatientModel.IsRegisteredToPortal()}
                        onSendToPortal={(model, unsignedFileName, signedFileName) => this.onExamRequestPortalClick(
                            RequestsInboxItem.fromLabRequest(model as LabRequest),
                            unsignedFileName,
                            signedFileName
                        )}
                        onToggleIsInvalid={(id, encounterId, isInvalid, sharedItemId) => sessionStore.dispatch(
                            LocalPatientOperations.MedicalNote.LabRequestPopup_ToggleIsInvalid,
                            {
                                requestId: id,
                                encounterId: encounterId,
                                isInvalid: isInvalid,
                                patientContactId: this.props.NoteModel.get("PatientContactId"),
                                sharedItemId: sharedItemId
                            }
                        )}
                        onSignClick={model => {
                            this.mutateState(
                                s => s.showLabRequestPopupUpgrade = false,
                                () => this.openSignExamRequestPopup(RequestsInboxItem.fromLabRequest(model as LabRequest), ExportMode.Sign)
                            );
                        }}
                        loadSignedAuthorizations={requestId => patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                            { requestIds: [requestId, ...MedicalNoteHelper.getExamRequestIds(this.props.NoteModel)] }
                        )}
                        patientContactIdForNewRequests={this.props.PatientModel.IndividualResponse.Contact_id}
                    />
                )}

                {this.state.showConsultationPopupUpgrade && (
                    <MedicalRequestPopupController
                        type={MedicalRequestType.ConsultationRequest}
                        id={this.state.selectedConsultationRequestId}
                        encounter={{
                            id: noteModel.get("Encounter").Id,
                            consultationDate: noteModel.get("Encounter").ConsultationDate,
                            isSignedOff: noteModel.get("Encounter").IsSignedOff,
                            SignedByEmployeeId: signedOffByEmployeeId,
                            SignedOffDate: signedOffDate
                        }}
                        patientDoB={this.props.PatientModel.IndividualResponse.DateOfBirth}
                        closeAfterButtonClick={true}
                        onClose={() => this.closeConsultationRequestPopupUpgrade()}
                        onSaveSuccess={(model, shouldSaveRelatedEmployee, printInfo) => {
                            let item = ConsultationRequestsInboxItem.fromConsultationRequest(model as ConsultationRequest, printInfo);
                            sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConsultationRequestPopup_OnSaveSucces, {
                                item: item,
                                patientContactId: this.props.NoteModel.get("PatientContactId"),
                                shouldSaveRelatedEmployee: shouldSaveRelatedEmployee
                            });
                        }}
                        onDeleteSuccess={(requestId, encounterId) => patientStore.onConsultationRequestDeletedSuccess(requestId, encounterId)}
                        onPrintSuccess={(fileName, requestId) => this.onMedicalRequestPrintSuccess(fileName, requestId, MedicalRequestType.ConsultationRequest)}
                        onToggleIsInvalid={(id, encounterId, isInvalid, sharedItemId) => sessionStore.dispatch(
                            LocalPatientOperations.MedicalNote.ConsultationRequestPopup_ToggleIsInvalid,
                            {
                                consultationRequestId: id,
                                encounterId: encounterId,
                                isInvalid: isInvalid,
                                patientContactId: this.props.NoteModel.get("PatientContactId"),
                                sharedItemId: sharedItemId
                            }
                        )}
                        onSendToPortal={(model, unsignedFileName, signedFileName) => this.onConsultationRequestPortalClick(
                            ConsultationRequestsInboxItem.fromConsultationRequest(model as ConsultationRequest),
                            unsignedFileName,
                            signedFileName
                        )}
                        isPatientRegisteredToPortal={this.props.PatientModel.IsRegisteredToPortal()}
                        onSignClick={model => {
                            this.mutateState(
                                s => s.showConsultationPopupUpgrade = false,
                                () => this.openSignConsultationRequestPopup(ConsultationRequestsInboxItem.fromConsultationRequest(model as ConsultationRequest), ExportMode.Sign)
                            );
                        }}
                        loadSignedAuthorizations={consultationRequestId => patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                            { consultationRequestIds: [consultationRequestId, ...MedicalNoteHelper.getConsultationRequestIds(this.props.NoteModel)] }
                        )}
                        patientContactIdForNewRequests={this.props.PatientModel.IndividualResponse.Contact_id}
                    />
                )}

                {noteModel.get("IsPrivateBillPopupOpen") === true && (
                    <PrivateBillPopup
                        Model={noteModel.get("PrivateBillPopupModel")}
                        BillableServicesModel={noteModel.get("PrivateBillBillableServices").toArray()}
                        OnCloseOperation={LocalPatientOperations.MedicalNote.ClosePrivateBillPopup}
                        DisplayFullWidth={true}
                        PrintOp={LocalPatientOperations.MedicalNote.Billing_Print}
                        CloseInvalidOp={LocalPatientOperations.MedicalNote.Billing_CloseInvalidate}
                        SaveInvalidationOp={LocalPatientOperations.MedicalNote.Billing_InvalidateSave}
                        InvalidateOp={LocalPatientOperations.MedicalNote.Billing_Invalidate}
                        InvalidationChangeOp={LocalPatientOperations.MedicalNote.Billing_InvalidateChange}
                        PatientModel={this.props.PatientModel}
                        IsPopup={true}
                        DisableButtons={false}
                        OnButtonClick={null}
                    />
                )}

                {noteModel.get("ShowInvalidBillPopup") === true && (
                    <Popup title={cacheStore.GetString("InvalidateBillLabel")} cssClass="double-booking" closeTarget={LocalPatientOperations.MedicalNote.Billing_Invalidate}>
                        <div className="p-body">
                            <textarea className="form-control" data-cy="medicalNote-invalidateBillNote" value={noteModel.get("InvalidBillNote")} onChange={e => this.onNoteInvalidationPrivateBillChange(e)} id="history_create_invalidation" disabled={false} rows={4} style={{ maxHeight: "300px", marginTop: "5px" }} />
                        </div>
                        <div className="p-footer flex-display flex-end">
                            <ButtonGroup>
                                <ButtonControl
                                    onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.Billing_Invalidate)}
                                    label={cacheStore.GetString("CancelLabel")}
                                    className="btn btn-default"
                                />
                                <ButtonControl
                                    dataCy="medical-note-billing-invalidation-save"
                                    onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.Billing_SaveInvalidation)}
                                    label={cacheStore.GetString("SaveLabel")}
                                    className="btn btn-primary"
                                />
                            </ButtonGroup>
                        </div>
                    </Popup>
                )}

                {noteModel.get("IsPrivateBillEditPopupOpen") === true && (
                    <PrivateBillEditPopup
                        Model={noteModel.get("PrivateBillPopupModel")}
                        BillableServicesModel={noteModel.get("PrivateBillBillableServices").toArray()}
                        OnPrintAndSaveOperation={null}
                        OnCloseOperation={LocalPatientOperations.MedicalNote.ClosePrivateBillEditPopup}
                        OnSaveOperation={LocalPatientOperations.MedicalNote.PrivateBillWarning_Save}
                        OnCloseBillOperation={LocalPatientOperations.MedicalNote.OnCloseBill}
                        TodayClickedOp={LocalPatientOperations.MedicalNote.Billing_OnTodayClick}
                        DateOfVisit_Set={LocalPatientOperations.MedicalNote.Billing_DateOfVisit_Set}
                        SelectClinician={LocalPatientOperations.MedicalNote.Billing_SelectClinician}
                        DidMountOp={LocalPatientOperations.MedicalNote.Billing_LoadDummyServices}
                        SiteSelectedOp={LocalPatientOperations.MedicalNote.Billing_SiteChange}
                        CostCenterChangedOp={LocalPatientOperations.MedicalNote.Billing_OnCostCenterChange}
                        PaymentMethodChangedOp={LocalPatientOperations.MedicalNote.Billing_OnSeletedPaymentChange}
                        DiscountChangedOp={LocalPatientOperations.MedicalNote.Billing_OnDiscountChange}
                        DiscountTypeChangeOp={LocalPatientOperations.MedicalNote.Billing_ChangeDiscountType}
                        ClearOp={LocalPatientOperations.MedicalNote.Billing_ClearSelection}
                        SelectService={LocalPatientOperations.MedicalNote.Billing_SelectBillableService}
                        PriceChange={LocalPatientOperations.MedicalNote.Billing_OnPriceChange}
                        QuantityChanged={LocalPatientOperations.MedicalNote.Billing_OnQuantityChange}
                        ChangeGST={LocalPatientOperations.MedicalNote.Billing_ChangeHasGST}
                        ChangeQST={LocalPatientOperations.MedicalNote.Billing_ChangeHasQST}
                        ChangeHST={LocalPatientOperations.MedicalNote.Billing_ChangeHasHST}
                        ChangeGenericTax={LocalPatientOperations.MedicalNote.Billing_ChangeHasGenericTax}
                        RemoveService={LocalPatientOperations.MedicalNote.Billing_RemoveService}
                        NotesChangeOp={LocalPatientOperations.MedicalNote.Billing_NotesChange}
                        LockProviderOp={LocalPatientOperations.MedicalNote.Billing_LockProvider}
                        ChangeRecipientDetailsOp={LocalPatientOperations.MedicalNote.Billing_OnRecipientDetailsChange}
                        BilledToChangeOp={LocalPatientOperations.MedicalNote.Billing_OnBilledToChange}
                        OnCancelBillOp={LocalPatientOperations.History.PrivateBill_CloseCancelPopup}
                        DoNotRenderAsScrollable={true}
                        onIsFormValidChange={() => { }}
                    />
                )}

                {noteModel.get("ShowReusingBillPopup") === true && (
                    <ReusingPrivateBillPopup
                        onCloseCallback={() => self.closeReusingBillPopup()}
                        NoteModel={this.props.NoteModel}
                    />
                )}

                {noteModel.get("IsPrivateBillCreationWarningPopupOpen") === true &&
                    <PrivateBillCreationWarningPopup NoteModel={noteModel} />
                }

                {summaryModel.get("ShowNewNoteWarning") === true &&
                    <NewMedicalNoteWarningPopup SummaryModel={this.props.MedicalSummaryModel} />
                }

                {summaryModel.get("ShowNoteCreationSpecialityErrorWarning") === true &&
                    <NewMedicalNoteNoSpecialityWarningPopup SummaryModel={this.props.MedicalSummaryModel} />
                }

                {noteModel.get("AlreadySignedOffShowWarningPopup") === true &&
                    <MedicalNoteAlreadySignedOffWarningPopup NoteModel={this.props.NoteModel} />
                }

                {noteModel.get("AutosaveFailed") === true &&
                    <MedicalNoteAutosaveAlreadSignedOffWarningPopup NoteModel={this.props.NoteModel} />
                }

                {noteModel.get("SpecialtyMedfarBillingPopup") === true &&
                    <MedfarBillingSpecialtyPopup onCloseCallback={() => this.closeSpecialtyMedfarBillingPopup()} patientId={this.props.PatientId} />
                }

                {noteModel.get("ShowApprovalSupervisorPopup") === true && (
                    <ResidentApprovalPopup
                        title={cacheStore.GetString("SendForApprovalLabel")}
                        onCloseCallback={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.CloseApprovalSupervisorPopup)}
                        onSelect={this.props.NoteModel.get("ShowApprovalSupervisorPopupOkButtonCallBack")}
                        SelectButtonLabel={this.props.NoteModel.get("ShowApprovalSupervisorPopupOkButtonLabel")}
                        ShowMessageBox={true}
                        EncounterIdSearchPendingApprouval={this.props.NoteModel.get("Encounter").Id}
                        allowlist={currentEmployeeMedicalNoteAllowlist}
                    />
                )}
                {this.state.showCRDSSupervisorPopup && (
                    <ResidentApprovalPopup
                        title={cacheStore.GetString("SelectSupervisorLabel")}
                        onCloseCallback={() => this.mutateState(s => s.showCRDSSupervisorPopup = false)}
                        onSelect={this.state.showCRDSSupervisorPopupCallback}
                        SelectButtonLabel={cacheStore.GetString("ContinueLabel")}
                        ShowMessageBox={false}
                        SupervisorSelectionIsOptional={true}
                        allowlist={currentEmployeeMedicalNoteAllowlist}
                    />
                )}
                {(noteModel.get("IsPrintSignaturePreviewPopupOpen") && sessionStore.signOnPrintAccess) && (
                    <PrintAndSignaturePopup
                        previewPdfUrl={this.props.NoteModel.get("PrintSignaturePreviewFileName")}
                        onClose={() => this.onClosePrintPreviewPopup()}
                        onSuccessPin={() => this.onGeneratePdfPreview(
                            this.props.NoteModel.get("PrintSignatureObjectId"),
                            this.props.NoteModel.get("PrintSignatureObjectType"),
                            true,
                            GenerateFileContext.PreviewForSignatureOnPrint
                        )}
                        onRejectPin={() => this.onGeneratePdfPreview(
                            this.props.NoteModel.get("PrintSignatureObjectId"),
                            this.props.NoteModel.get("PrintSignatureObjectType"),
                            false,
                            GenerateFileContext.PreviewForSignatureOnPrint
                        )}
                        onPrint={() => {
                            const printUrl = this.props.NoteModel.get("PrintSignaturePreviewFileName");
                            if (printUrl) {
                                this.openBrowserPrintPreview(printUrl);
                                sessionStore.dispatch(LocalPatientOperations.MedicalNote.CloseSignPopup_ForPrint);
                            }
                        }}
                        hasPin
                    />
                )}
                {(noteModel.get("IsSignaturePreviewPopupOpen") && sessionStore.signOnPrintAccess) && (
                    <PrintAndSignaturePopup
                        previewPdfUrl={this.props.NoteModel.get("PrintSignaturePreviewFileName")}
                        onClose={() => this.onClosePrintPreviewPopup()}
                        onSuccessPin={() => this.onGeneratePdfPreview(
                            this.props.NoteModel.get("PrintSignatureObjectId"),
                            this.props.NoteModel.get("PrintSignatureObjectType"),
                            true,
                            GenerateFileContext.PreviewForSignatureOnPrint
                        )}
                        onRejectPin={() => this.onGeneratePdfPreview(
                            this.props.NoteModel.get("PrintSignatureObjectId"),
                            this.props.NoteModel.get("PrintSignatureObjectType"),
                            false,
                            GenerateFileContext.PreviewForSignatureOnPrint
                        )}
                        onSign={() => {
                        }}
                        hasPin
                    />
                )}
            </div>
        );
    }

    private RenderMedicalNoteColumn(): JSX.Element {
        const model = this.props.NoteModel;
        const encounter = model.get("Encounter");

        if (encounter.EncounterRelatedEmployees == null)
            encounter.EncounterRelatedEmployees = [];

        const canDelete = model.get("EncounterIsDeletable");
        const encounterUnapprouvability = model.get("EncounterUnapprouvability");

        const showEditButtons = this.canEdit && !this.isEditMode;
        const showSaveButtons = this.isEditMode;
        const showUnapproveButton = !showEditButtons && !showSaveButtons && encounterUnapprouvability && encounterUnapprouvability.CanUnapprouve;

        const canUnapprouve = encounterUnapprouvability && encounterUnapprouvability.CanUnapprouve && encounter.IsSignedOff == true;
        const showCantUnApproveReason = !canUnapprouve && encounterUnapprouvability && EncounterUnapprouvability.ShouldDisplayReason(encounterUnapprouvability.NoteLockedReasons) && encounter.IsSignedOff == true;
        const hasFooter = !this.props.isReadOnly && (canDelete || showCantUnApproveReason || showUnapproveButton || showEditButtons || showSaveButtons);

        const hvarvalue =
            showEditButtons ||
                showSaveButtons ||
                (encounterUnapprouvability && encounterUnapprouvability.CanUnapprouve) ||
                (
                    encounterUnapprouvability && !encounterUnapprouvability.CanUnapprouve &&
                    EncounterUnapprouvability.ShouldDisplayReason(encounterUnapprouvability.NoteLockedReasons)
                )
                ? 60 : 0;

        const isEditDisabled = model.get("AutosaveFailedSignoffDate") != null || (model.get("AutosaveFailedSignoffUser") != null && model.get("AutosaveFailedSignoffUser").length > 0);
        const isSignoffDisabled = this.IsSignoffDisabled();
        const scrollWidth = (this.medicalNoteScrollerDivElement?.offsetWidth ?? 0) - (this.medicalNoteScrollerDivElement?.clientWidth ?? 0);

        const currentEmployee = sessionStore.getCurrentEmployee();
        const shouldHaveSupervisorBasedOnTitle = currentEmployee.shouldHaveSupervisorBasedOnTitle();
        const shouldHaveSupervisorBasedOnTitleAndNoteContext = shouldHaveSupervisorBasedOnTitle && this.isNoteContextRestrictedForSupervisedUsers();

        const currentBillingProvider = currentEmployee.getBillingProviderResolved();

        try {
            return (
                <div
                    medical-note-main-container="true"
                    note-id={this.props.NoteModel.get("Encounter").Id}
                    className="medical-summary-main-container medical-note-main-container"
                    onClick={() => {
                        if (this.state.tmpEmployeeDurationEdit == true)
                            this.onAcceptTimerModif(this.state.tmpEmployeeDurationEditValue);
                    }}
                >
                    {this.props?.isHighlighting &&
                        !IsNullOrWhiteSpace(this.props.MedicalSummaryModel?.get("EncounterSearchTerm") ?? "") &&
                        !isArrayNullOrUndefinedOrEmpty(this.state.allElementsToHighlight) &&
                        (
                            <SearchBoxControl
                                searchText={this.props.MedicalSummaryModel?.get("EncounterSearchTerm") ?? ""}
                                container={this.medicalNoteScrollerDivElement}
                                elementsRects={this.state.allElementsToHighlight ?? []}
                                currentScrollTop={this.highlightService?.currentScrollTop ?? 0}
                                topInPX={this.medicalNoteScrollerDivElement?.offsetTop ?? 0}
                                rightInPX={51 + scrollWidth}
                                encounterSearchTermChangedOperation={LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged}
                                setHighlightServiceIndexCallbackFunc={(curIndex: number) => {
                                    if (this.highlightService) {
                                        this.highlightService.index = curIndex;
                                        this.highlightService.execute(null, false);
                                    }
                                }}
                                scrollToRatio={2}
                            />
                        )
                    }

                    <div
                        id="medical-note-container"
                        className={"medical-note-container " + (this.props?.isHighlighting ? this.medicalNoteContainerClassName : "")}
                    >
                        <div
                            data-scrolling
                            data-hvar={hvarvalue}
                            id="medicalNoteScroller"
                            className={"medical-note-section scrollable " + (this.props.isHighlighting ? "highlight-container" : "")}
                            ref={element => this.medicalNoteScrollerDivElement = element}
                        >
                            {encounter && (
                                <MedicalNoteHeader
                                    encounter={encounter}
                                    noteModel={this.props.NoteModel}
                                    patientUIModel={this.props.PatientUIModel}
                                    employeeDurationEdit={this.state.tmpEmployeeDurationEdit}
                                    employeeDurationEditValue={this.state.tmpEmployeeDurationEditValue}
                                    isMedicalSummaryPanelDisplayed={this.state.showMedicalSummaryPanel}
                                    isReadOnly={this.props.isReadOnly}
                                    onMedicalSummaryLinkClick={() => this.toggleMedicalSummaryPanel()}
                                    onEditConsultationDetailsClick={(consultationContextLookupId?: string) => this.OnEditConsultationDetailsClick(consultationContextLookupId)}
                                    onOpenSecondScreenClick={() => this.openSecondScreen()}
                                    onPrintMedicalNote={() => this.openFaxPrintEncounterExportableFilePopup(ExportMode.Print)}
                                    onShowMedicalNoteFaxPopup={() => this.openFaxPrintEncounterExportableFilePopup(ExportMode.Fax)}
                                    onTimerChangeCallback={value => this.mutateState(s => {
                                        s.tmpEmployeeDurationEditValue = new TimeSpan();
                                        s.tmpEmployeeDurationEditValue.AddHours(value.getHours());
                                        s.tmpEmployeeDurationEditValue.AddMinutes(value.getMinutes());
                                        s.tmpEmployeeDurationEditValue.AddSeconds(value.getSeconds());
                                    })}
                                    onTimerEnterCallBack={value => {
                                        let tmpTimeSpan = new TimeSpan();
                                        tmpTimeSpan.AddHours(value.getHours());
                                        tmpTimeSpan.AddMinutes(value.getMinutes());
                                        tmpTimeSpan.AddSeconds(value.getSeconds());
                                        this.onAcceptTimerModif(tmpTimeSpan);
                                    }}
                                    onTimerEscapeCallBack={() => this.mutateState(s => s.tmpEmployeeDurationEdit = false)}
                                    onTimerClick={() => {
                                        const currentEmployee = Encounter.GetCurrentRelatedEmployee(encounter);

                                        if (currentEmployee) {
                                            this.mutateState(s => {
                                                s.tmpEmployeeDurationEditValue = TimeSpan.FromTicksStr(currentEmployee.DurationTicks ?? sessionStore.timerCurrentValue.ticks.toString());
                                                s.tmpEmployeeDurationEdit = true;
                                            });
                                        }
                                    }}
                                />
                            )}
                            <MedicalSummarySection
                                noteModel={this.props.NoteModel}
                                medicalSummaryModel={this.props.MedicalSummaryModel}
                                dateOfBirth={this.props.PatientModel.IndividualResponse.DateOfBirth}
                                vitalSignPercentileUtility={this.vitalSignPercentileUtility}
                                immunizations={this.state.immunizations}
                                immunizationSyncPopupProps={this.state.immunizationSyncPopupProps}
                                isEditMode={this.isEditMode}
                                isReadOnly={this.props.isReadOnly}
                                toggleSectionOpen={() => this.mutateState(s => {
                                    s.medicalNoteSections = {
                                        ...s.medicalNoteSections,
                                        isMedicalSummarySectionExpanded: !s.medicalNoteSections.isMedicalSummarySectionExpanded
                                    };
                                }, () => this.toggleSectionOpen("medicalsummary"))}
                                onMedicalItemClick={(item, view) => this.onMedicalItemClick(item, view)}
                                addMedicalHistoryItem={() => this.AddMedicalHistoryItem()}
                                addVitalSignItem={() => this.AddVitalSignItem()}
                                onRvqStatusClick={(immunization, event) => this.onRvqStatusClick(immunization, event)}
                                isSectionOpen={this.state.medicalNoteSections.isMedicalSummarySectionExpanded}
                            />
                            <ProgressNote
                                noteModel={this.props.NoteModel}
                                isEditMode={this.isEditMode}
                                isReadOnly={this.props.isReadOnly}
                                handwrittenNotes={() => this.GetHandwrittenNoteRenders(this.props.NoteModel)}
                                loadCurrentVitalSign={() => this.loadCurrentVitalSign(this.props.NoteModel)}
                                toggleAutoSave={() => this.ToggleAutoSave()}
                                toggleSectionOpen={() => this.mutateState(s => {
                                    s.medicalNoteSections = {
                                        ...s.medicalNoteSections,
                                        isProgressNoteSectionExpanded: !s.medicalNoteSections.isProgressNoteSectionExpanded
                                    };
                                }, () => this.toggleSectionOpen("progressnote"))}
                                showNotePopup={(context, event) => this.ShowNotePopup(context, event)}
                                closeClickableText={() => this.closeClickableText()}
                                onAutosaveFieldBlur={() => this.OnAutosaveFieldBlur()}
                                showClickableVisibility={(text, event) => this.showClickableVisibility(text, event)}
                                addVitalSignItem={() => this.AddVitalSignItem()}
                                displayHandNoteUI={(encounterId: string) => this.displayHandNoteUI(encounterId)}
                                isSectionOpen={this.state.medicalNoteSections.isProgressNoteSectionExpanded}
                            />
                            <MedicalNoteForm
                                radPdfFormSelectionPopupOpen={this.state.radPdfFormSelectionPopupOpen}
                                noteModel={this.props.NoteModel}
                                medicalSummaryModel={this.props.MedicalSummaryModel}
                                patientModel={this.props.PatientModel}
                                immunizations={this.state.immunizations}
                                currentEncounter={this.currentEncounter}
                                canEdit={this.canEdit}
                                isReadOnly={this.props.isReadOnly}
                                isEditMode={this.isEditMode}
                                isShowWorkSafeBcInvalidFieldsPopup={this.state.showWorkSafeBcInvalidFieldsPopup}
                                patientId={this.props.PatientId}
                                crdsSelectedSupervisorContactId={this.state.crdsSelectedSupervisorContactId}
                                formsPopupOpen={this.state.formsPopupOpen}
                                formsPopupType={this.state.formsPopupType}
                                isSectionOpen={this.state.medicalNoteSections.isFormsSectionExpanded}
                                displayContext={this.props.displayContext}
                                mutateChangeFormPropsState={(formsPopupOpen: boolean, formsPpopupType: FormType) =>
                                    this.mutateState(s => {
                                        s.formsPopupOpen = formsPopupOpen;
                                        s.formsPopupType = formsPpopupType;
                                    })
                                }
                                getRevokeSignaturePopupModel={(id: string, type: SignedAuthorizationDocumentType) => {
                                    this.getRevokeSignaturePopupModel(id, type);
                                }}
                                createNewWorkSafeBcFormClaim={() => this.openCreateEncounterClaimWindowAsync(currentBillingProvider)}
                                setShowWorkSafeBcInvalidFieldsPopup={(isVisible: boolean) => {
                                    this.mutateState(s => s.showWorkSafeBcInvalidFieldsPopup = isVisible);
                                }}
                                onOpenAPSSForm={() => this.onAddAPSSFormClick()}
                                onAddFormClick={() => this.onAddFormClick()}
                                workSafeBcController={this.workSafeBcController}
                                toggleSectionOpen={() => this.mutateState(s => {
                                    s.medicalNoteSections = {
                                        ...s.medicalNoteSections,
                                        isFormsSectionExpanded: !s.medicalNoteSections.isFormsSectionExpanded
                                    };
                                }, () => this.toggleSectionOpen("forms"))}
                                onCloseRadPdfFormSelectionPopup={() => this.closeRadPdfFormSelectionPopup()}
                                onContinueRadPdfFormSelectionPopup={() => this.closeRadPdfFormSelectionPopup()}
                                onDuplicateRadPdfFormSelectionPopup={() => this.closeRadPdfFormSelectionPopup()}
                            />
                            <AssessmentSection
                                isEditMode={this.isEditMode}
                                isReadOnly={this.props.isReadOnly}
                                noteModel={this.props.NoteModel}
                                patientModel={this.props.PatientModel}
                                toggleSectionOpen={() => this.mutateState(s => {
                                    s.medicalNoteSections = {
                                        ...s.medicalNoteSections,
                                        isAssesmentSectionExpanded: !s.medicalNoteSections.isAssesmentSectionExpanded
                                    };
                                }, () => this.toggleSectionOpen("assesment"))}
                                addMedicalHistoryItem={this.AddMedicalHistoryItem}
                                onMedicalItemClick={(item, view) => this.onMedicalItemClick(item, view, true)}
                                isSectionOpen={this.state.medicalNoteSections.isAssesmentSectionExpanded}
                                createFlowsheet={this.CreateFlowsheet}
                                onFlowsheetClick={flowsheet => sessionStore.dispatch(
                                    LocalPatientOperations.UI.ChangeVerticalTabs,
                                    {
                                        value: PatientTab.Flowsheet,
                                        isFlowsheetTabActive: true,
                                        flowsheetSummary: flowsheet
                                    }
                                )}
                            />
                            <PlanSection
                                noteModel={this.props.NoteModel}
                                summaryModel={this.props.MedicalSummaryModel}
                                patientModel={this.props.PatientModel}
                                patientId={this.props.PatientId}
                                isEditMode={this.isEditMode}
                                isReadOnly={this.props.isReadOnly}
                                preferredPharmacy={this.props.PreferredPharmacy}
                                toggleSectionOpen={() => this.mutateState(s => {
                                    s.medicalNoteSections = {
                                        ...s.medicalNoteSections,
                                        isPlanSectionExpanded: !s.medicalNoteSections.isPlanSectionExpanded
                                    };
                                }, () => this.toggleSectionOpen("plan"))}
                                toggleAutoSave={() => this.ToggleAutoSave()}
                                openConsultationRequestPopup={id => this.openConsultationRequestPopupUpgrade(id)}
                                openLabRequestPopup={(id, type) => this.openLabRequestPopupUpgrade(id, type)}
                                openSignExamRequestPopup={(model, exportMode) => this.openSignExamRequestPopup(model, exportMode)}
                                openSignConsultationRequestPopup={(model, exportMode) => this.openSignConsultationRequestPopup(model, exportMode)}
                                onExamRequestPortalClick={exam => this.onExamRequestPortalClick(exam)}
                                onConsultationRequestPortalClick={consultation => this.onConsultationRequestPortalClick(consultation)}
                                getRevokeSignaturePopupModel={(id, type) => this.getRevokeSignaturePopupModel(id, type)}
                                showNotePopup={e => this.ShowNotePopup("OTH", e)}
                                showClickableVisibility={e => this.showClickableVisibility("followupplan", e)}
                                onAutosaveFieldBlur={() => this.OnAutosaveFieldBlur()}
                                onCloseClickableText={() => this.closeClickableText()}
                                isSectionOpen={this.state.medicalNoteSections.isPlanSectionExpanded}
                            />
                            {!sessionStore.getCurrentEmployee().isActiveSiteMexico() && (
                                <BillingClaimSection
                                    noteModel={this.props.NoteModel}
                                    isReadOnly={this.props.isReadOnly}
                                    patientId={this.props.PatientId}
                                    billingProvider={currentBillingProvider}
                                    onCreateNewClaimCallBack={() => this.createNewClaimAsync(currentBillingProvider)}
                                    usePatientBillingHistory={this.shouldUsePatientBillingHistory(currentBillingProvider)}
                                />
                            )}
                        </div>
                        {!cacheStore.ClinicSettings.EnableSecondScreen && this.renderAIScribeLaunchButton()}
                        {cacheStore.ClinicSettings.EnableSecondScreen && !this.props.isReadOnly && (
                            <div className={`fxheight animation-container-in-note ${this.state.showMedicalSummaryPanel ? "open" : ""}`} data-hvar={-hvarvalue} data-cy="medicalNote-animationContainer">
                                <div onClick={() => this.toggleMedicalSummaryPanel()} className="medical-summary-handle">
                                    <div className="slide-menu-icon blue centered-toggler" data-cy="medicalNote-menuIcon">
                                        <div>
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                    {this.renderAIScribeLaunchButton()}
                                </div>
                                {this.state.showMedicalSummaryPanel && (
                                    <div
                                        className="medical-summary-panel-container-in-note scrollable"
                                        data-scrolling
                                    >
                                        <MedicalSummaryPanel
                                            patientContactId={this.props.PatientId}
                                            cssClass="containerMedicalNote"
                                            context={MedicalSummaryPanelContext.MedicalNote}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {hasFooter && (
                        <div className="meta-footer" data-cy="medicalNote-footer">
                            {canDelete == true && (
                                <ButtonControl
                                    dataCy="medicalNote-delete"
                                    className="btn btn-danger pull-left"
                                    label={cacheStore.GetString("DeleteEncounterLabel")}
                                    onClick={() => this.deleteMedicalNote()}
                                />
                            )}

                            {showEditButtons == true && (
                                <div>
                                    {!shouldHaveSupervisorBasedOnTitleAndNoteContext
                                        && (
                                            <ButtonControl
                                                dataCy="medicalNote-link-approve"
                                                className="btn btn-primary pull-right margin-xs-left"
                                                disabled={isEditDisabled || isSignoffDisabled}
                                                onClick={() => this.signOffNoteWithFlowsheetWarningCheck()}
                                                label={cacheStore.GetString("ApproveLabel")}
                                            />
                                        )
                                    }

                                    {shouldHaveSupervisorBasedOnTitleAndNoteContext && (
                                        <ButtonControl
                                            dataCy="medicalNote-send"
                                            className="btn btn-primary pull-right margin-xs-left"
                                            onClick={() => {
                                                sessionStore.dispatch(LocalPatientOperations.MedicalNote.OpenSupervisorSelectionPopup, {
                                                    onOkCallback: (supervisorContactId, message, allowlist) => patientStore.ActionsMedicalNote.SendPendingApproval(supervisorContactId, this.props.NoteModel.get("Encounter").Id, message, allowlist)
                                                });
                                            }}
                                            label={cacheStore.GetString("SendLabel")}
                                        />
                                    )}

                                    <ButtonControl
                                        className="btn btn-primary pull-right"
                                        disabled={isEditDisabled}
                                        label={cacheStore.GetString("ModifyLabel")}
                                        dataCy="medicalNote-modify"
                                        onClick={() => this.onModifyButtonClick()}
                                    />
                                </div>
                            )}

                            {showSaveButtons == true && (
                                <div>
                                    <ButtonControl
                                        dataCy="medicalNote-save"
                                        disabled={isEditDisabled}
                                        onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote)}
                                        label={cacheStore.GetString("SaveLabel")}
                                        className="btn btn-primary pull-right margin-xs-left"
                                    />

                                    {shouldHaveSupervisorBasedOnTitle && (
                                        <ButtonControl
                                            dataCy="medicalNote-save-send"
                                            onClick={() => {
                                                sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote, {
                                                    then: () => {
                                                        sessionStore.dispatch(LocalPatientOperations.MedicalNote.OpenSupervisorSelectionPopup, {
                                                            onOkCallback: (supervisorContactId, message, allowlist) => {
                                                                patientStore.ActionsMedicalNote.SendPendingApproval(supervisorContactId, this.props.NoteModel.get("Encounter").Id, message, allowlist);
                                                            }
                                                        });
                                                    }
                                                });
                                            }}
                                            className="btn btn-primary pull-right margin-xs-left"
                                            label={cacheStore.GetString("SaveAndSendLabel")}
                                        />
                                    )}
                                    <ButtonControl
                                        onClick={() => this.cancelNote()}
                                        label={cacheStore.GetString("CancelLabel")}
                                        className="btn btn-default pull-right"
                                        dataCy="medicalNote-cancel"
                                    />
                                </div>
                            )}

                            {canUnapprouve == true && (
                                <ButtonControl
                                    onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ShowUnapproveNoteWarningPopup)}
                                    label={cacheStore.GetString("OpenLabel")}
                                    className="btn btn-default pull-right margin-xs-left"
                                    dataCy="medicalNote-open"
                                />
                            )}

                            {showCantUnApproveReason && encounterUnapprouvability.NoteLockedReasons.some(r => r == NoteLockedReason.NoteHasBeenPrintedOrFaxed) &&
                                <div className="pull-right">{EncounterUnapprouvability.GetReasonString(NoteLockedReason.NoteHasBeenPrintedOrFaxed)}</div>
                            }
                            {showCantUnApproveReason && encounterUnapprouvability.NoteLockedReasons.some(r => r == NoteLockedReason.SomeSummaryItemsAddedInThisNoteMayNotBeModifiedAsTheyAreNowPartOfMoreRecentNotes) &&
                                <div className="pull-right">{EncounterUnapprouvability.GetReasonString(NoteLockedReason.SomeSummaryItemsAddedInThisNoteMayNotBeModifiedAsTheyAreNowPartOfMoreRecentNotes)}</div>
                            }
                        </div>
                    )}
                    {this.state.isApproveFlowsheetsWarningVisible && cacheStore.ClinicSettings.EnableChronicDiseaseManagement && (
                        <ApproveEncounterFlowsheetConfirmationModal
                            onConfirm={() => {
                                this.mutateState(s => { s.isApproveFlowsheetsWarningVisible = false; });
                                this.signOffNote();
                            }}
                            onCancel={() => this.mutateState(s => { s.isApproveFlowsheetsWarningVisible = false; })}
                        />
                    )}
                </div>
            );
        }
        catch (error) {
            // This try catch should not be.
            // Before refactoring it we need to log production errors to understand the root cause of it.
            Client.postAsync<any>(
                ServerOperations.Server.Myle_LogFrontEndError,
                {
                    route: RouterHistory.location.pathname,
                    requestHistory: Client.getOperationLogs(),
                    selectedPatientContactId: patientStore.getCurrentPatientContactId(),
                    message: error.message,
                    stack: error.stack
                }
            );
            return <div />;
        }
    }

    private async onMedicalNoteCancelAutoApproveWarningPopupContinue(hideApproveWarningPopup: boolean): Promise<void> {
        this.mutateState(s => s.medicalNoteCancelAutoApproveWarningPopupOpen = false);
        await this.medicalNotePopupAction.cancelEncounterAutoApprove(this.currentEncounter.Id, hideApproveWarningPopup, this.state.selectedConsultationContextLookupId);

        this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup = hideApproveWarningPopup;

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettings, {
            userSettingsConfig: this.userSettings.UserSettingsConfig
        });

        if (!this.state.selectedConsultationContextLookupId) {
            this.state.medicalNoteCancelAutoApproveWarningPopupCallback();
        }
    }

    private async cancelEncounterAutoApprove(): Promise<void> {
        await this.medicalNotePopupAction.cancelEncounterAutoApprove(this.currentEncounter.Id);
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettings);
    }

    private closeRadPdfFormSelectionPopup(): void {
        this.mutateState(s => s.radPdfFormSelectionPopupOpen = false);
    }

    private async onAddAPSSFormClick(): Promise<void> {
        if (!this.currentEncounter.IsSignedOff && this.currentEncounter.AutoApprove) {
            if (this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
                await this.cancelEncounterAutoApprove();
            }
            else {
                this.mutateState(s => {
                    s.medicalNoteCancelAutoApproveWarningPopupOpen = true;
                    s.medicalNoteCancelAutoApproveWarningPopupCallback = () => this.openAPSSForm();
                });
            }
        }

        if (this.currentEncounter.IsSignedOff || !this.currentEncounter.AutoApprove || this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
            this.openAPSSForm();
        }
    }

    private async onAddFormClick(): Promise<void> {
        if (!this.currentEncounter.IsSignedOff && this.currentEncounter.AutoApprove) {
            if (this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
                await this.cancelEncounterAutoApprove();
            }
            else {
                this.mutateState(s => {
                    s.medicalNoteCancelAutoApproveWarningPopupOpen = true;
                    s.medicalNoteCancelAutoApproveWarningPopupCallback = () => this.mutateState(s => s.radPdfFormSelectionPopupOpen = true);
                });
            }
        }

        if (this.currentEncounter.IsSignedOff || !this.currentEncounter.AutoApprove || this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
            this.mutateState(s => {
                s.radPdfFormSelectionPopupOpen = true;
            });
        }
    }

    private async onModifyButtonClick(): Promise<void> {
        if (this.currentEncounter.AutoApprove) {
            if (this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
                await this.cancelEncounterAutoApprove();
            }
            else {
                this.mutateState(s => {
                    s.medicalNoteCancelAutoApproveWarningPopupOpen = true;
                    s.medicalNoteCancelAutoApproveWarningPopupCallback = () => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ModifyNote, null, 300);
                });
            }
        }

        if (!this.currentEncounter.AutoApprove || this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.ModifyNote, null, 300);
        }
    }

    private async onRvqStatusClick(immunization: MedicalItemImmunization, event: React.MouseEvent): Promise<void> {
        const xPos = event.clientX;
        const yPos = event.clientY;

        this.showSyncImmunizationPopup(immunization, xPos, yPos);
    }

    private showSyncImmunizationPopup(immunization: MedicalItemImmunization, xPos: number, yPos: number): void {
        const props: SyncRvqImmunizationPopupProps = {
            left: xPos,
            top: yPos,
            immunization,
            onClose: () => this.closeSyncImmunizationPopup()
        };

        this.mutateState(s => s.immunizationSyncPopupProps = props);
    }

    private closeSyncImmunizationPopup(): void {
        this.mutateState(s => s.immunizationSyncPopupProps = null);
    }

    private loadCurrentVitalSign(noteModel: IPatientMedicalNoteModel): JSX.Element {
        const vitalSign = this.getCurrentVitalSign(noteModel);
        return vitalSign ? (
            <VitalSignSummary
                vSign={vitalSign}
                dateOfBirth={this.props.PatientModel.IndividualResponse.DateOfBirth}
                encounterId={this.props.NoteModel.get("Encounter").Id}
                vitalSignPercentileUtility={this.vitalSignPercentileUtility}
                onMedicalItemClick={(item, view) => this.onMedicalItemClick(item, view)}
                addVitalSign={() => this.AddVitalSignItem()}
            />
        ) :
            null;
    }

    private getCurrentVitalSign(noteModel: IPatientMedicalNoteModel): MedicalItemVitalSign {
        const allItems = this.props.MedicalSummaryModel.get("MedicalItemsVitalSign").toArray();
        if (allItems == null)
            return null;

        const vitalSigns = allItems.filter(x => x.RelatedEncounterId == noteModel.get("Encounter").Id);
        if (vitalSigns.length == 0)
            return null;

        return vitalSigns.sortByPropertyDesc(x => x.CommitDate).firstOrDefault();
    }

    private async loadGrowthCharts(): Promise<void> {
        const growthChartDataModels = await GrowthChartApiClient.getGrowthChartsCacheData();
        this.vitalSignPercentileUtility = new VitalSignPercentileUtility(growthChartDataModels, this.getVitalSignPatientData());
    }

    private calculatePercentile(state: MedicalNoteState, property: VitalProperty, vitalSign: MedicalItemVitalSign = null): void {
        if (!state.percentiles)
            state.percentiles = {};

        state.percentiles[property] = this.vitalSignPercentileUtility?.calculatePercentileFromMedicalItemVitalSign(property, vitalSign);
    }

    private calculateAllPercentiles(vitalSign: MedicalItemVitalSign = null): void {
        this.mutateState(state => {
            this.calculatePercentile(state, "Height", vitalSign);
            this.calculatePercentile(state, "Weight", vitalSign);
            this.calculatePercentile(state, "MassBodyIndex", vitalSign);
            this.calculatePercentile(state, "HeadCircumference", vitalSign);
        });
    }

    private getVitalSignPatientData(): VitalSignPatientData {
        const patient = this.props.PatientModel;
        return {
            dateOfBirth: patient?.IndividualResponse?.DateOfBirth,
            gender: patient?.Gender(),
            ethnicityKeys: patient?.IndividualResponse?.EthnicityKeys,
            contactFacetId: patient?.InactivableResponse.Id,
            contactId: patient?.IndividualResponse.Contact_id,
            isPremature: patient?.IndividualResponse?.IsBornPremature,
            bornPrematureWeeks: patient?.IndividualResponse?.BornPrematureWeeks
        };
    }

    private openSignExamRequestPopup(model: RequestsInboxItem, exportMode: ExportMode = ExportMode.Print): void {
        ExportableFileDispatcher.openSignFaxPrintPopupForExamRequest({
            patientContactId: this.props.PatientId,
            requestId: model.RequestId,
            attachedDocuments: model.AttachedDocuments,
            requestDate: model.RequestDate,
            exportMode,
            isPortalSyncEnabled: this.props.PatientModel.IsRegisteredToPortal() && !this.isSelectedExamRequestSyncedToPortal(),
            signedPrintAuthorizations: model.SignedPrintAuthorizations,
            requestingStaffContactFacetId: model.RequestingStaffContactFacetId,
            createdByContactId: model.CreatedByContactId,
            onSignButtonCallback: () => patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                { requestIds: MedicalNoteHelper.getExamRequestIds(this.props.NoteModel) }
            ),
            syncToPortalAfterAction: (exportableFile, unsignedFileName: string, signedFileName: string) => this.onExamRequestPortalClick(
                model,
                unsignedFileName,
                signedFileName,
                exportableFile.hasSignature()
            ),
            mutateState: mutation => this.mutateState(s => mutation(s)),
            requestRecipient: model.Recipient,
            recipientContactDirectoryEntityId: model.RecipientContactDirectoryEntityId,
            recipientContactDirectoryEntityType: model.RecipientContactDirectoryEntityType
        });
    }

    private openSignConsultationRequestPopup(model: ConsultationRequestsInboxItem, exportMode: ExportMode = ExportMode.Print): void {
        ExportableFileDispatcher.openSignFaxPrintPopupForConsultationRequest({
            patientContactId: this.props.PatientId,
            consultationRequestId: model.ConsultationRequestId,
            attachedDocuments: model.AttachedDocuments,
            consultationRequestDate: model.ConsultationRequestDate,
            exportMode,
            isPortalSyncEnabled: this.props.PatientModel.IsRegisteredToPortal() && !this.isSelectedConsultationRequestSyncedToPortal(),
            signedPrintAuthorizations: model.SignedPrintAuthorizations,
            requestingStaffContactFacetId: model.RequestingStaffContactFacetId,
            createdByContactId: model.CreatedByContactId,
            onSignButtonCallback: () => patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                { consultationRequestIds: MedicalNoteHelper.getConsultationRequestIds(this.props.NoteModel) }
            ),
            syncToPortalAfterAction: (exportableFile, unsignedFileName: string, signedFileName: string) => this.onConsultationRequestPortalClick(
                model,
                unsignedFileName,
                signedFileName,
                exportableFile.hasSignature()
            ),
            defaultContext: this.props.PatientModel.IntervenorsOnFile,
            mutateState: mutation => this.mutateState(s => mutation(s)),
            requestRecipient: model.Recipient,
            recipientContactDirectoryEntityId: model.RecipientContactDirectoryEntityId,
            recipientContactDirectoryEntityType: model.RecipientContactDirectoryEntityType,
            exportableFileGenerationIntent: ExportableFileGenerationIntent.Print
        });
    }

    private openFaxPrintEncounterExportableFilePopup(exportMode: ExportMode): void {
        if (exportMode === ExportMode.Print) {
            const exportableFile = new FileTypeEncounter({
                EncounterId: this.currentEncounter.Id,
                description: cacheStore.GetString("NoteDLabel"),
                date: DateTime.now()
            });
            exportableFile.print();
            return;
        }

        ExportableFileDispatcher.openSignFaxPrintPopupForEncounter({
            patientContactId: this.props.PatientId,
            encounterId: this.currentEncounter.Id,
            exportMode: exportMode,
            defaultContext: this.props.PatientModel.IntervenorsOnFile
        });
    }

    private GetHandwrittenNoteRenders(noteModel: IPatientMedicalNoteModel): JSX.Element[] {
        const handwrittenNotes: FreeFormImage[] = noteModel.get("HandwrittenNotes").toArray();
        const isEditMode = this.isEditMode;
        const ImageStyle: React.CSSProperties = isEditMode ? { cursor: "pointer" } : {};
        const noCache = noteModel.get("IsHandWrittenNoteModified");
        const noCacheGuid = noteModel.get("HandWrittenNoteVersion");

        if (noCache) {
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.EnableHandWrittenNotesCache, {
                PatientId: this.props.PatientId
            });
        }

        return handwrittenNotes.map(note => {
            const imageUrl = apiSettings.buildAppUrl(`api/patient/encounter/note/handwritten/fetch/${note.Id}`, { auth: true, noCache: noCache }) + "&" + noCacheGuid;

            return (
                <div key={note.Id} className="hand-note-img-container">
                    {isEditMode && (
                        <div className="img-del-abs"><span className="glyphicon glyphicon-remove red-text" onClick={() => this.deleteHandNote(note)} /></div>
                    )}
                    <img
                        className="img-responsive"
                        src={imageUrl}
                        style={ImageStyle}
                        onClick={() => this.editHandNote(note)}
                    />
                </div>
            );
        });
    }

    private closeSpecialtyMedfarBillingPopup(): void {
        LocalEvent.Dispatch(LocalPatientOperations.MedfarBilling.CloseSpecialityPopup);
        sessionStore.hideGlobalOverlay();
    }

    private showCriteria(): void {
        LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchCriteriaDisplayToggle);
    }

    private ToggleDisplayOnlyUnsigned(): void {
        LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.ToggleDisplayOnlyUnsigned);
    }

    private onEncounterClick(encounterId: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterClick, {
            value: encounterId
        });
    }

    private toggleSectionOpen(section: string): void {
        if (!this.props.isReadOnly) {
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.ToggleOpenSection, {
                value: section
            });
        }
    }

    private ShowNotePopup(noteContext: string, e: React.MouseEvent): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.ShowNotePopup, {
            event: e,
            noteContext: noteContext
        });
    }

    private closeReusingBillPopup(): void {
        LocalEvent.Dispatch(LocalPatientOperations.MedicalNote.CloseReusingBillPopup);
    }

    private newNote(e: React.MouseEvent): void {
        e.preventDefault();

        if (this.props.NoteModel.get("ViewMode") == MedicalNoteViewMode.Edit) {
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote); // Save First
        }
        else {
            sessionStore.dispatch(LocalPatientOperations.MedicalSummary.NewNote);
        }
    }

    private onMedicalItemClick(item: MedicalItem, view: MedicalItemsTab, fromAssessment: boolean = false): void {
        PatientMedicalSummaryHelper.setMedicalItemPreviousRoute(view, item.EMRTemplateMedicalItemSubType);

        var operation = new LocalEvent(LocalPatientOperations.MedicalNote.SwitchToDetails);
        operation.AddItem("viewMode", view);
        operation.AddItem("isFromAssessment", fromAssessment);
        operation.AddItem("selectedEMRTemplateId", item ? item.EMRTemplateId : "");

        LocalEventDispatcher.dispatch(operation);
    }

    private closeClickableText(): void {
        if (this.props?.isReadOnly) {
            return;
        }

        var operation = new LocalEvent(LocalPatientOperations.MedicalNote.Edit_RemoveClickableTextVisibility);
        LocalEventDispatcher.dispatch(operation);
    }

    private showClickableVisibility(clickableText: string, e: React.MouseEvent): void {
        if (e != null)
            e.stopPropagation();

        var operation = new LocalEvent(LocalPatientOperations.MedicalNote.Edit_ShowClickableTextVisibility);
        operation.AddItem("value", clickableText);
        LocalEventDispatcher.dispatch(operation);
    }

    private async OnEditConsultationDetailsClick(consultationContextLookupId?: string): Promise<void> {
        this.mutateState(s => s.selectedConsultationContextLookupId = consultationContextLookupId);
        if (!this.currentEncounter.IsSignedOff && this.currentEncounter.AutoApprove) {
            if (this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
                await this.cancelEncounterAutoApprove();
            }
            else {
                this.mutateState(s => {
                    s.medicalNoteCancelAutoApproveWarningPopupOpen = true;
                    s.medicalNoteCancelAutoApproveWarningPopupCallback = () => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConsultationDetailsPopupShow, null, 300);
                });
            }
        }

        if (this.currentEncounter.IsSignedOff || !this.currentEncounter.AutoApprove || this.userSettings.UserSettingsConfig.HideAutoApproveWarningPopup) {
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConsultationDetailsPopupShow, null, 300);
        }
    }

    private AddMedicalHistoryItem(isFromAssessment: boolean = false): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem);

        operation.AddItem("isFromAssessment", isFromAssessment);
        LocalEventDispatcher.dispatch(operation);
    }

    private AddVitalSignItem(): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicalNote.Edit_NewVitalSignItem);
        LocalEventDispatcher.dispatch(operation);
    }

    private async CreateFlowsheet(flowsheetTemplate: FlowsheetTemplate, patientId: Guid, encounterId: Guid): Promise<void> {
        const request: CreateFlowsheetRequest = {
            TemplateId: flowsheetTemplate.Id,
            PatientId: patientId,
            EncounterId: encounterId
        };

        const response = await FlowsheetClient.createFlowsheet(request);

        if (response.isSuccess && response.data) {
            const flowsheetSummary = new FlowsheetSummary({
                Id: response.data.Id,
                EncounterId: encounterId,
                ApprovalDate: null,
                ApprovedByEmployeeId: null,
                Template: flowsheetTemplate,
                CreationDate: null,
                CreatedByEmployeeId: null
            });

            sessionStore.dispatch(
                LocalPatientOperations.Flowsheets.AddFlowsheetSummary,
                { flowsheet: flowsheetSummary }
            );

            sessionStore.dispatch(
                LocalPatientOperations.UI.ChangeVerticalTabs,
                {
                    value: PatientTab.Flowsheet,
                    isFlowsheetTabActive: true,
                    flowsheetSummary: flowsheetSummary
                }
            );
        }
        else {
            sessionStore.showPopup(response.errors);
        }
    }

    private displayHandNoteUI(encounterId: string): void {
        if (this.handNoteWindow == null || this.handNoteWindow.closed) {
            this.handNoteWindow = window.open(rootPath + "/handnote?encounterId=" + encounterId, "_blank");
        }
    }

    private openAPSSForm(): void {
        if (sessionStore.getCurrentEmployee().shouldHaveSupervisorBasedOnTitle()) {
            // Open ResidentSupervisorSelection
            this.mutateState(s => {
                s.showCRDSSupervisorPopup = true;
                s.showCRDSSupervisorPopupCallback =
                    supervisorContactId => {
                        // onSupervisorSelected in the SupervisorSelectionPopup
                        this.mutateState(
                            sClosing => {
                                sClosing.showCRDSSupervisorPopup = false;
                                sClosing.showCRDSSupervisorPopupCallback = null;
                            },

                            // THEN, Open Apss Popup Form Selection in the context of the selected Supervisor
                            () => this.mutateState(s => {
                                s.formsPopupOpen = true;
                                s.formsPopupType = FormType.APSS;
                                s.crdsSelectedSupervisorContactId = supervisorContactId;
                            })
                        );
                    };
            });
        }
        else {
            this.mutateState(s => {
                s.formsPopupOpen = true;
                s.formsPopupType = FormType.APSS;
                s.crdsSelectedSupervisorContactId = null;
            });
        }
    }

    private IsSignoffDisabled(): boolean {
        var cannotSignoff = false;
        var currentEmployeeTitle = sessionStore.myleEmployeeTitle;
        var encounter = this.props.NoteModel.get("Encounter");

        if (EmployeeTitleHelper.isStudent(currentEmployeeTitle)
            || currentEmployeeTitle === EmployeeTitle.Unknown
            || currentEmployeeTitle === EmployeeTitle.NONE) {
            if (encounter.ConsultationContextLookupId == this.houseVisitLookupId.LookupID
                || encounter.ConsultationContextLookupId == this.clinicalVisitLookupId.LookupID
                || encounter.ConsultationContextLookupId == this.webcamConsultationLookupId.LookupID
                || encounter.ConsultationContextLookupId == this.summaryUpdateLookupId.LookupID
                || encounter.ConsultationContextLookupId == this.prescriptionLookupId.LookupID) {
                cannotSignoff = true;
            }
        }
        return cannotSignoff;
    }

    private cancelNote(): void {
        if (this.handNoteWindow != null) {
            this.handNoteWindow.close();
            this.handNoteWindow = null;
        }
        LocalEvent.Dispatch(LocalPatientOperations.MedicalNote.CancelEditNote);
    }

    private editHandNote(handNoteItem: FreeFormImage): void {
        if (this.handNoteWindow == null || this.handNoteWindow.closed) {
            this.handNoteWindow = window.open(rootPath + "/handnote?encounterId=" + this.props.NoteModel.get("Encounter").Id, "_blank");

            this.handNoteWindow.onload = () => {
                const operation = new LocalEvent(LocalOperations.HandNote.LoadNoteForEdit);
                operation.AddItem("note", handNoteItem);
                this.handNoteWindowDispatcher.dispatch(operation);
            };
        }
    }

    private miscDispatcherHandler(event: LocalEvent): void {
        if (event.Operation === LocalOperations.HandNote.Ready) {
            var wdw: any = this.handNoteWindow;
            this.handNoteWindowDispatcher = wdw.HandNoteModule.localNoteDispatcher as Dispatcher<any>; // we have dispatcher
        }
    }

    private deleteHandNote(handNoteItem: FreeFormImage): void {
        var confirmMsg = cacheStore.GetString("IrreversibleActionLabel");
        if (confirm(confirmMsg)) {
            var o = new LocalEvent(LocalPatientOperations.MedicalNote.DeleteHandNote);
            o.AddItem("note", handNoteItem);
            LocalEventDispatcher.dispatch(o);
        }
    }

    private ToggleAutoSave(shouldSave: boolean = true): void {
        this.shouldAutosave = shouldSave;
    }

    private OnAutosaveFieldBlur(): void {
        this.AutoSaveTextFields(true);
    }

    private AutoSaveTextFields(force: boolean = false): void {
        let model = this.props.NoteModel;
        let encounter = model.get("Encounter");
        if (this.shouldAutosave || force) {
            if (encounter != null) {
                let requestData = {
                    EncounterId: encounter.Id,
                    ReasonForVisit: encounter.VisitPurpose,
                    History: encounter.HistoricalNote,
                    Exam: encounter.ExamenNote,
                    Imaging: encounter.RadiologyNote,
                    Lab: encounter.LabNote,
                    OtherNoteAndIntervention: encounter.ProgressNote,
                    FollowupPlan: encounter.OtherPlanNote
                };

                // verify if changed since last save
                if (deepEquality(requestData, this.lastSucessfullEncounterRequest))
                    return;

                // perform a copy before Client.Post alterate the request.
                const lastEncounterRequest = cloneDeep(requestData);

                Client.Post(
                    ServerOperations.Server.Myle_Patient_MedicalNote_AutoSaveEncounter,
                    requestData,
                    (response: AutoSaveEncounterResponse) => {
                        if (!response?.IsSuccess) {
                            // check if the response failed because it was already signed off and popup warning to tell the user
                            if (response?.EncounterAlreadySignedoffUserContactFacetId) {
                                const patientNoteModel = patientStore.PatientMedicalNoteModels[this.props.PatientId]
                                    .set("AutosaveFailedSignoffDate", response.EncounterAlreadySignedoffDateTime)
                                    .set("AutosaveFailedSignoffUser", response.EncounterAlreadySignedoffUserContactFacetId)
                                    .set("AutosaveFailed", true);

                                patientStore.PatientMedicalNoteModels[this.props.PatientId] = patientNoteModel;
                                patientStore.EmitChangeToListeners();
                            }
                        }
                        else {
                            // save in state last sucessfull encounter request
                            this.lastSucessfullEncounterRequest = lastEncounterRequest;

                            // auto save success
                            patientStore.updateContributorsEncountersData(this.props.PatientId, response.EncounterId);

                            if (SecondScreenNoteService.getInstance().isChildWindowOpen())
                                SecondScreenNoteService.getInstance().debounceUpdateModel(new SecondScreenNoteData({
                                    patientId: this.props.PatientId,
                                    encounterId: response.EncounterId
                                }));
                        }
                    }
                );
            }

            this.ToggleAutoSave(false);
        }
    }

    private toggleLeftMenu(): void {
        let operation = this.props.NoteModel.get("LeftMenuOpen") === true ? LocalPatientOperations.MedicalNote.CollapseLeftMenu : LocalPatientOperations.MedicalNote.ExpandLeftMenu;
        LocalEvent.Dispatch(operation);
    }

    // --------------------------------------------------------------------------------------------------
    // External Billing Methods
    // --------------------------------------------------------------------------------------------------

    // create claims
    private async createNewClaimAsync(provider: BillingProvider): Promise<void> {
        if (BillingProviderHelper.shouldUseLegacyMedfarBilling(provider)) {
            this.createNewMedfarClaim();
            return;
        }

        if (BillingProviderHelper.shouldUseLegacyFacturationNetBilling(provider)) {
            this.createNewFacturationNetClaim();
            return;
        }

        if (this.isWorkSafeBcFormBillingEnabled(provider)) {
            const formIsValid = await this.validateWorkSafeBcFormForClaim();

            if (!formIsValid) {
                this.mutateState(s => s.showWorkSafeBcInvalidFieldsPopup = true);
                return;
            }
        }

        await this.openCreateEncounterClaimWindowAsync(provider);
    }

    private async openCreateEncounterClaimWindowAsync(provider: BillingProvider): Promise<void> {
        const primaryIntervenorEmployeeIds = this.props.ClinicianModel.get("IntervenorsOnFile")?.toArray()
            .filter(intervenor => intervenor.Model?.Intervenor.IsPrimary)
            .map(intervenor => intervenor.Model?.Intervenor.EmployeeId);

        const claimReload = this.getCreateClaimReloadFunction(provider);

        await ExternalBillingManager.openCreateEncounterClaimWindowAsync(
            this.currentEncounter,
            provider,
            await sessionStore.getCurrentEmployee().getExternalBillingUserAsync(),
            claimReload,
            null,
            primaryIntervenorEmployeeIds
        );
    }

    private async createNewMedfarClaim(): Promise<void> {
        const licence = sessionStore.getCurrentEmployee().Model.FirstLicenceNumber;
        if (IsNullOrWhiteSpace(licence)) {
            sessionStore.showPopup({
                title: cacheStore.GetString("LicenceErrorLabel"),
                content: [cacheStore.GetString("LicenceErrorMessageLabel"), `Licence : ${licence || "--"}`]
            });
            return;
        }

        const response = await Client.postAsync<{ HasSpecialtyCode: boolean, IsSpecialist: boolean }>(
            ServerOperations.Server.MedfarBilling_CheckIfHasSpecialtyCode,
            { Licence: licence }
        );

        if (response.data.HasSpecialtyCode === true) {
            patientStore.PatientMedicalNoteModels[this.props.PatientId] = patientStore.PatientMedicalNoteModels[this.props.PatientId]
                .set("IsMedfarBillingSpecialist", response.data.IsSpecialist);

            sessionStore.dispatch(LocalPatientOperations.MedfarBilling.PrepareNewBill);
        }
        else {
            patientStore.PatientMedicalNoteModels[this.props.PatientId] = patientStore.PatientMedicalNoteModels[this.props.PatientId]
                .set("SpecialtyMedfarBillingPopup", true);
        }
        patientStore.EmitChangeToListeners();
    }

    private createNewFacturationNetClaim(): void {
        const createBill = (): void => FacturationNetManager.createBill(this.currentEncounter, this.props.PatientId);

        if (sessionStore.isScheduledVisitChecked == null)
            sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.ShowVisitTypeSelectionPopup, { callback: createBill });
        else
            createBill();
    }

    private async validateWorkSafeBcFormForClaim(): Promise<boolean> {
        const currentFormLight = this.props.NoteModel.get("WorkSafeBcForms").firstOrDefault(p => p.EncounterId === this.currentEncounter.Id);

        // return valid if there is no form
        // You can't have an invalid form if you don't have a form 👉😏
        if (!currentFormLight)
            return true;

        // save the current form to ensure it's available for the billing handler
        await this.workSafeBcController?.current?.saveForm();

        const currentForm = await this.getWorkSafeBcForm(currentFormLight.Id);

        if (!currentForm)
            return true;

        const workerModel = WorkSafeBcWorkerModel.fromPatient(this.props.PatientModel);
        const validator = new WorkSafeBcFormValidator(currentForm, workerModel);

        return validator.isFormValidForClaim();
    }

    // --------------------------------------------------------------------------------------------------
    // signature and prescription related methods :
    // --------------------------------------------------------------------------------------------------

    private onClosePrintPreviewPopup(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.CloseSignPopup_ForPrint);
    }

    /**
     * Open a PDF preview UI.
     * @param documentId The document ID to preview.
     * @param documentType The document type to preview.
     * @param withSignature Whether to add a signature to the PDF.
     * @param generateFileContext The context for which the PDF is being generated.
     */
    private onGeneratePdfPreview(documentId: string, documentType: MyleObjectType, withSignature: boolean, generateFileContext: GenerateFileContext): void {
        switch (documentType) {
            case MyleObjectType.Prescription:
                sessionStore.dispatch(LocalPatientOperations.Fax.GeneratePrescriptionPDF, {
                    prescriptionId: documentId,
                    withSignature: withSignature,
                    isForQuebec: sessionStore.getCurrentEmployee().isActiveSiteQuebec()
                });
                break;
            case MyleObjectType.ConsultationRequest:
                sessionStore.dispatch(
                    LocalPatientOperations.MedicalNote.GenerateConsultationRequestPdf,
                    {
                        faxConsultationRequestComposite: {
                            RequestId: documentId,
                            WithSignature: withSignature
                        },
                        generateFileContext: generateFileContext
                    }
                );
                break;
            case MyleObjectType.ExamRequest:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.GenerateLabRequestPdf, {
                    faxLabRequestComposite: {
                        RequestId: documentId,
                        WithSignature: withSignature,
                        LabFax: this.state.genericFaxInfoFax || "",
                        LabName: this.state.genericFaxInfoName || ""
                    },
                    generateFileContext: generateFileContext
                });
                break;
        }
    }

    private openBrowserPrintPreview(previewPdfUrl: string): void {
        previewPdfUrl && PrintHelper.ShowPrintPreviewFileName(previewPdfUrl);
    }

    // --------------------------------------------------------------------------------------------------
    // Lab request popup fax
    // --------------------------------------------------------------------------------------------------

    private openLabRequestPopupUpgrade(id?: string, type?: LabRequestType): void {
        this.mutateState(s => {
            s.showLabRequestPopupUpgrade = true;
            s.selectedLabRequestId = id;
            s.selectedLabRequestType = type ? type : LabRequestType.None;
        });
    }

    private closeLabRequestPopupUpgrade(then?: () => void): void {
        this.mutateState(
            s => {
                s.showLabRequestPopupUpgrade = false;
                s.selectedLabRequestId = null;
                s.selectedLabRequestType = LabRequestType.None;
            },
            then
        );
    }

    private onExamRequestPortalClick(examRequest: RequestsInboxItem, unsignedFileName?: string, signedFileName?: string, skipSignature: boolean = false): void {
        const showWarningToSyncToPortal = PortalHelper.showWarningToSyncToPortal(
            examRequest.SharedItem,
            examRequest.RequestingStaffContactFacetId,
            examRequest.CreatedByContactId,
            SignedPrintAuthorization.getLast(examRequest.SignedPrintAuthorizations)
        );

        if (showWarningToSyncToPortal) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: [cacheStore.GetString("WarningSignRequestingStaffPortalLabel")]
            });
        }
        else {
            examRequest.SiteId = this.currentEncounter.SiteId;
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.Request_PortalClick, {
                portalShareableItem: examRequest,
                unsignedFileName: unsignedFileName,
                signedFileName: signedFileName,
                skipSignature
            });
        }
    }

    private onConsultationRequestPortalClick(consultationRequestsInboxItem: ConsultationRequestsInboxItem, unsignedFileName?: string, signedFileName?: string, skipSignature: boolean = false): void {
        const showWarningToSyncToPortal = PortalHelper.showWarningToSyncToPortal(
            consultationRequestsInboxItem.SharedItem,
            consultationRequestsInboxItem.RequestingStaffContactFacetId,
            consultationRequestsInboxItem.CreatedByContactId,
            SignedPrintAuthorization.getLast(consultationRequestsInboxItem.SignedPrintAuthorizations)
        );

        if (showWarningToSyncToPortal) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: [cacheStore.GetString("WarningSignRequestingStaffPortalLabel")]
            });
        }
        else {
            consultationRequestsInboxItem.SiteId = this.currentEncounter.SiteId;
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.Request_PortalClick, {
                portalShareableItem: consultationRequestsInboxItem,
                unsignedFileName: unsignedFileName,
                signedFileName: signedFileName,
                skipSignature
            });
        }
    }

    private onNoteInvalidationPrivateBillChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        var op = new LocalEvent(LocalPatientOperations.MedicalNote.Billing_InvalidNoteChange);
        op.AddItem("InvalidNote", e.target.value);
        LocalEventDispatcher.dispatch(op);
    }

    private onAcceptTimerModif(timeSpanValue: TimeSpan): void {
        let self = this;

        let noteModel = self.props.NoteModel;
        let employeeId = sessionStore.getCurrentEmployee().ContactFacetId;

        if (noteModel.get("ViewMode") == MedicalNoteViewMode.Edit)
            sessionStore.setEncounterTimer(new TimerComponentModel(self.props.PatientId, this.currentEncounter.Id, timeSpanValue), false);

        this.currentEncounter.EncounterRelatedEmployees = this.currentEncounter.EncounterRelatedEmployees.map(rel => {
            if (rel.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId)
                rel.DurationTicks = timeSpanValue.ticks.toString();
            return rel;
        });
        noteModel = noteModel.set("Encounter", this.currentEncounter);

        Client.Post(ServerOperations.Server.Myle_SaveEncounterTimeSpent, {
            EmployeeId: employeeId,
            EncounterId: this.currentEncounter.Id,
            Ticks: timeSpanValue.ticks
        }, () => null);

        self.mutateState(s => {
            s.tmpEmployeeDurationEdit = false;
            s.tmpEmployeeDurationEditValue = null;
        });
    }

    private closeConsultationRequestPopupUpgrade(): void {
        this.mutateState(s => {
            s.showConsultationPopupUpgrade = false;
            s.selectedConsultationRequestId = null;
        });
    }

    private openConsultationRequestPopupUpgrade(consultationId: string): void {
        this.mutateState(s => {
            s.showConsultationPopupUpgrade = true;
            s.selectedConsultationRequestId = consultationId;
        });
    }

    private async onMedicalRequestPrintSuccess(fileName: ExportableFile, id: string, medicalRequestType: MedicalRequestType): Promise<void> {
        if (!fileName)
            return;

        PrintHelper.ShowPrintPreviewFileName((await fileName.getFetchUrlDtoAsync()).FetchUrl);

        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.UpdateScreeningsModelAfterPrintingRequest,
            {
                requestId: id,
                requestType: medicalRequestType
            }
        );
    }

    private redirectFromAttachedCommunicationsPopup(attachedCommunication: AttachedCommunication, context: AttachedCommunicationType): void {
        let operation: number;

        switch (context) {
            case AttachedCommunicationType.Recall:
                operation = LocalPatientOperations.History.RecallRedirectFromEncounter;
                break;
            case AttachedCommunicationType.Message:
                operation = LocalPatientOperations.History.MessageRedirectFromEncounter;
                break;
            case AttachedCommunicationType.Task:
                operation = LocalPatientOperations.History.TaskRedirectFromEncounterOrLabs;
                break;
        }

        sessionStore.dispatch(
            operation,
            { attachedCommunications: [attachedCommunication] }
        );

        sessionStore.hideGlobalOverlay();
    }

    private closeAttachedCommunicationsPopup(): void {
        sessionStore.dispatch(LocalPatientOperations.History.AttachedCommunicationsClose);
    }

    private getSendToPortalStrategy(portalLinkedItemModel: IPortalLinkedItemModel): BaseSendMedicalNoteItemToPortalPopupStrategy {
        const linkedItemType = portalLinkedItemModel.get("LinkedItemType");

        const strategy = sharedItemProviderFactory.get(linkedItemType).createSendMedicalNoteItemToPortalPopupStrategy(
            toPortalPopupModel(this.props.NoteModel),
            () => this.onSendToPortalSignatureAccepted(),
            () => this.onRemoveSignature()
        );

        if (!strategy)
            throw new Error(`No strategy found for linked item type: ${linkedItemType}`);

        return strategy;
    }

    private getPortalPopupRender(): JSX.Element {
        const portalLinkedItemModel = this.props.NoteModel.get("PortalLinkedItemModel");

        return (
            <SendToPortalPopup
                model={portalLinkedItemModel}
                strategy={this.getSendToPortalStrategy(portalLinkedItemModel)}
            />
        );
    }

    private isSelectedConsultationRequestSyncedToPortal(): boolean {
        if (this.state.selectedConsultationRequestId) {
            const consultationRequest = this.props.NoteModel.get("ConsultationRequests").find(x => x.ConsultationRequestId === this.state.selectedConsultationRequestId);
            if (consultationRequest) {
                const portalStatus = PortalHelper.getLinkedItemPortalStatus(consultationRequest.getSharedItem());
                return portalStatus === PortalSyncStatus.Pending || portalStatus === PortalSyncStatus.Viewed;
            }
        }
        return false;
    }

    private isSelectedExamRequestSyncedToPortal(): boolean {
        if (this.state.selectedLabRequestId) {
            const examRequest = this.props.NoteModel.get("Requests").find(x => x.RequestId === this.state.selectedLabRequestId);
            if (examRequest) {
                const portalStatus = PortalHelper.getLinkedItemPortalStatus(examRequest.getSharedItem());
                return portalStatus === PortalSyncStatus.Pending || portalStatus === PortalSyncStatus.Viewed;
            }
        }
        return false;
    }

    private onSendToPortalSignatureAccepted(): void {
        this.mutateState(
            s => s.requestPortalPinValid = true,
            () => this.onGeneratePdfPreview(
                this.props.NoteModel.get("PortalLinkedItemModel").get("LinkedItemModel").getPortalObjectId(),
                this.props.NoteModel.get("PortalSyncPopupType"),
                true,
                GenerateFileContext.PreviewPortalSync
            )
        );
    }

    /**
     * Remove signature from exportable file on Send to portal
     */
    private onRemoveSignature(): void {
        this.onGeneratePdfPreview(
            this.props.NoteModel.get("PortalLinkedItemModel").get("LinkedItemModel").getPortalObjectId(),
            this.props.NoteModel.get("PortalSyncPopupType"),
            false,
            GenerateFileContext.PreviewPortalSync
        );
    }

    /**
     * Resume the note timer if it stopped due to changing between patients.
     */
    private ResumeTimer(): void {
        const shouldResume =
            this.props.NoteModel.get("ViewMode") == MedicalNoteViewMode.Edit
            && !sessionStore.timerComponentModel?.isCounting;

        if (shouldResume) {
            const contactId = patientStore.getCurrentPatient()?.IndividualResponse?.Contact_id;
            const encounterId = this.currentEncounter?.Id;

            if (contactId && encounterId) {
                sessionStore.setEncounterTimer(new TimerComponentModel(contactId, encounterId));
            }
        }
    }

    private isWorkSafeBcFormBillingEnabled(provider: BillingProvider): boolean {
        return BillingProviderHelper.getBillingProviderSupportsWorkSafeBcForms(provider)
            && this.isWorkSafeBcFormEnabled();
    }

    private isWorkSafeBcFormEnabled(): boolean {
        const encounterSite = cacheStore.GetSiteById(this.currentEncounter.SiteId);

        return cacheStore.ClinicSettings.EnableWorkSafeBcFormFeature
            && encounterSite.isSiteInProvince(CanadaState.BritishColumbia);
    }

    private async getWorkSafeBcForm(formId: string): Promise<WorkSafeBcPhysicianReportModel> {
        const response = await Client.postAsync<GetWorkSafeBcPhysicianReportFormResponse>(
            ServerOperations.Server.WorkSafeBc_GetPhysicianReportForm,
            { Id: formId }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        return new WorkSafeBcPhysicianReportModel(response.data.PhysicianReport);
    }

    private get canEdit(): boolean {
        return !this.props.NoteModel.get("Encounter").IsSignedOff && !this.props.isReadOnly;
    }

    private get isEditMode(): boolean {
        const model = this.props.NoteModel;
        return this.canEdit
            && model.get("ViewMode") === MedicalNoteViewMode.Edit;
    }

    private openSecondScreen(): void {
        SecondScreenHelper.openWindow({ patientId: patientStore.getCurrentPatientContactId() });
    }

    private getRevokeSignaturePopupModel(id: string, type: SignedAuthorizationDocumentType): void {
        sessionStore.showPopup(new PopupModel({
            title: cacheStore.GetString("RevokeSignatureLabel"),
            content: cacheStore.GetString("RevokeSignatureWarningMessageLabel"),
            showCancel: true,
            okLabel: cacheStore.GetString("RevokeLabel"),
            okAction: this.revokeSignatureFromCopy.bind(this, id, type),
            okVariant: "error"
        }));
    }

    private async revokeSignatureFromCopy(id: string, type: SignedAuthorizationDocumentType): Promise<void> {
        const requestData = {
            Id: id,
            DocumentType: type,
            EmployeeId: sessionStore.getCurrentEmployee().ContactFacetId,
            EncounterId: this.props.NoteModel.get("Encounter").Id
        };
        const response = await Client.postAsync(ServerOperations.Server.Myle_RevokeSignaturePrintAuthorization, requestData);

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        const exportableFilePopupSetting = ExportableFilePopupSettingModel.fromCacheModel(cacheStore.UserSettings.ExportableFilePopup);
        switch (type) {
            case SignedAuthorizationDocumentType.ExamRequest:
                exportableFilePopupSetting.examRequest.exportableFileSignatureSettings = [];
                this.updateExportableFilePopupSetting(exportableFilePopupSetting);
                break;

            case SignedAuthorizationDocumentType.ConsultationRequest:
                exportableFilePopupSetting.consultationRequest.exportableFileSignatureSettings = [];
                this.updateExportableFilePopupSetting(exportableFilePopupSetting);
                break;

            case SignedAuthorizationDocumentType.Prescription:
                exportableFilePopupSetting.prescription.exportableFileSignatureSettings = [];
                this.updateExportableFilePopupSetting(exportableFilePopupSetting);
                break;
        }

        patientStore.ActionsMedicalNote.LoadSignedAuthorizations({
            prescriptonIds: MedicalNoteHelper.getPrescriptiondIds(this.props.MedicalSummaryModel.get("Prescriptions"), this.currentEncounter.Id),
            requestIds: MedicalNoteHelper.getExamRequestIds(this.props.NoteModel),
            consultationRequestIds: MedicalNoteHelper.getConsultationRequestIds(this.props.NoteModel),
            patientFormIds: MedicalNoteHelper.getPatientFormsIds(this.props.NoteModel),
            encounterId: this.props.NoteModel.get("Encounter").Id
        });
    }

    private updateExportableFilePopupSetting(exportableFilePopupSetting: ExportableFilePopupSettingModel): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettingsExportableFilePopup, {
            settings: exportableFilePopupSetting
        });
    }

    private toggleMedicalSummaryPanel(): void {
        this.mutateState(s => s.showMedicalSummaryPanel = !s.showMedicalSummaryPanel, () => {
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                cacheStore?.UserSettings?.MedicalNoteMedicalSummaryPanelSettings,
                this.state.showMedicalSummaryPanel,
                MedicalSummaryPanelContext.MedicalNote
            );
        });
    }

    private traverseMedicalNotesNodes(node: ChildNode, isUserInfo: boolean, searchText: string, isOverflowing: boolean, width: number): DOMRect[] {
        const arr = [];

        if (node && !IsNullOrWhiteSpace(searchText)) {
            const nodeElement: Element = node as Element;
            const htmlElement: HTMLElement = node as HTMLElement;
            const htmlElementStyle = htmlElement ? getComputedStyle(htmlElement) : null;

            isUserInfo = isUserInfo || !!nodeElement?.classList?.contains("c2");
            isOverflowing = isOverflowing || htmlElementStyle?.overflow === "hidden";
            width = HighlightHelper.getWidth(htmlElement, isOverflowing, width);

            if (HighlightHelper.isBackgroundColorTransparent(htmlElementStyle?.backgroundColor)) {
                for (let i = 0; i < node?.childNodes?.length; i++) {
                    if (node.childNodes[i].nodeName === "#text") {
                        const textNode = node.childNodes[i] as Text;

                        if (isUserInfo && textNode?.length > 0) {
                            arr.push(...HighlightHelper.getTextRectsToHighlightWithChildNode(nodeElement, textNode, searchText, width));
                        }
                    }
                    else {
                        arr.push(...this.traverseMedicalNotesNodes(node.childNodes[i], isUserInfo, searchText, isOverflowing, width));
                    }
                }
            }
        }

        return arr;
    }

    private initializeHighlightService(): void {
        this.highlightService = new HighlightService(
            this.medicalNoteScrollerDivElement,
            this.medicalNoteScrollerDivElement,
            this.medicalNoteContainerClassName,
            this.props.MedicalSummaryModel?.get("EncounterSearchTerm") ?? "",
            (searchText: string) => this.traverseMedicalNotesNodes(this.medicalNoteScrollerDivElement, false, searchText, false, HighlightHelper.invalidWidth)
        );

        this.highlightService.initialize();
    }

    private executeHighlightService(searchText: string, isIndexReset: boolean): void {
        if (this.highlightService) {
            this.highlightService?.execute(searchText, isIndexReset);
            this.mutateState(s => s.allElementsToHighlight = [...this.highlightService.allElementsToHighlight]);
        }
    }

    private terminateHighlightService(): void {
        if (this.highlightService) {
            this.highlightService?.terminate();
            this.highlightService = null;
        }
    }

    private async signOffNoteWithFlowsheetWarningCheck(): Promise<void> {
        if (this.isAiScribePopupOpen()) {
            sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeWarningActiveAssistant);
        }
        else if (cacheStore.ClinicSettings.EnableChronicDiseaseManagement) {
            try {
                const getFlowsheetsCountResponse = await FlowsheetClient.getFlowsheetsCount(this.props.NoteModel.get("Encounter").Id);

                if (getFlowsheetsCountResponse.Count > 0) {
                    this.mutateState(s => { s.isApproveFlowsheetsWarningVisible = true; });
                }
                else {
                    this.signOffNote();
                }
            }
            catch {
                sessionStore.showPopup({
                    title: cacheStore.GetString("Flowsheets_WarningLabel"),
                    content: cacheStore.GetString("UnexpectedErrorMessage")
                });
                this.signOffNote();
            }
        }
        else {
            this.signOffNote();
        }
    }

    private signOffNote(): void {
        LinkingPopupService
            .promptForAdditionalSignOffInformation(
                this.props.PatientId,
                this.props.NoteModel.get("Encounter").SiteId,
                this.props.NoteModel.get("Encounter").AppointmentId,
                this.props.NoteModel.get("Encounter").SuggestedAppointmentLinkId
            )
            .then(info => {
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.SignoffNote, { additionalSignOffInformation: info });
            })
            .catch(p => p && console.error(p)); // prompt cancelled
    }

    private isNoteContextRestrictedForSupervisedUsers(): boolean {
        const encounter = this.props.NoteModel.get("Encounter");

        return encounter.ConsultationContextLookupId === this.houseVisitLookupId.LookupID
            || encounter.ConsultationContextLookupId === this.clinicalVisitLookupId.LookupID
            || encounter.ConsultationContextLookupId === this.webcamConsultationLookupId.LookupID
            || encounter.ConsultationContextLookupId === this.prescriptionLookupId.LookupID
            || encounter.ConsultationContextLookupId === this.summaryUpdateLookupId.LookupID;
    }

    private deleteMedicalNote(): void {
        if (this.isAiScribePopupOpen())
            sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeWarningActiveAssistant);
        else
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.ShowDeleteNoteWarningPopup);
    }

    private renderAIScribeLaunchButton(): JSX.Element {
        const showAIScribeLaunchButton = this.showAIScribeLaunchButton();
        const showAIScribeMarketingButton = this.showAIScribeMarketingButton();

        if ((!showAIScribeLaunchButton && !showAIScribeMarketingButton) || !this.props.NoteModel.get("Encounter"))
            return <></>;

        return (
            <AIScribeLaunchButton
                encounterId={this.props.NoteModel.get("Encounter").Id}
                patientContactId={this.props.NoteModel.get("PatientContactId")}
                patientContactFacetId={this.props.PatientModel.PatientResponse.ContactFacet_id}
                className={cacheStore.ClinicSettings.EnableSecondScreen ? "ai-scribe-floating-btn" : "ai-scribe-floating-btn-second-screen-disabled"}
                onAIScribeSubscriptionInactive={() => sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeSubscriptionExpiredPopup, {})}
                onAIScribeConsultationCreated={(
                    consultationId,
                    url,
                    bottomPos,
                    rightPos,
                    encounterId,
                    patientContactId,
                    patientContactFacetId
                ) => sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribePopup, {
                    aiScribeConsultationId: consultationId,
                    aiScribeUrl: url,
                    patientContactId: patientContactId,
                    patientContactFacetId: patientContactFacetId,
                    openBottomPos: bottomPos,
                    openRightPos: rightPos,
                    encounterId: encounterId
                })}
                onAIScribeMarketingPopupOpen={async (
                    bottomPos,
                    rightPos,
                    encounterId,
                    patientContactId,
                    patientContactFacetId
                ) => {
                    sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeMarketingPopup, {
                        patientContactId,
                        patientContactFacetId,
                        encounterId,
                        openBottomPos: bottomPos,
                        openRightPos: rightPos
                    });
                    const aiScribeAction = new AIScribeAction();
                    await aiScribeAction.LogMarketingPopupDisplayAsync();
                }}
                type={showAIScribeLaunchButton ? "consultation" : "marketing"}
            />
        );
    }

    private showAIScribeLaunchButton(): boolean {
        return cacheStore.ClinicSettings.EnableAIScribe
            && this.state.aiScribeModel?.get("AIScribeLinkedUser")
            && !this.state.aiScribeModel.get("ShowAIScribePopup")
            && this.props.NoteModel.get("ViewMode") === MedicalNoteViewMode.Edit;
    }

    private isAiScribePopupOpen(): boolean {
        return cacheStore.ClinicSettings.EnableAIScribe
            && this.state.aiScribeModel.get("ShowAIScribePopup")
            && this.state.aiScribeModel.get("EncounterId") === this.props.NoteModel.get("Encounter").Id;
    }

    private showAIScribeMarketingButton(): boolean {
        return cacheStore.ClinicSettings.EnableAIScribe
            && cacheStore.ClinicSettings.EnableAIScribeAlwaysOn
            && !this.state.aiScribeModel?.get("AIScribeLinkedUser")
            && !this.state.aiScribeModel?.get("DisableAIScribeMarketingPopup")
            && this.props.NoteModel.get("ViewMode") === MedicalNoteViewMode.Edit;
    }

    private shouldUsePatientBillingHistory(billingProvider: BillingProvider): boolean {
        return cacheStore.ClinicSettings.EnablePatientBillingHistory
            && BillingProviderHelper.getBillingProviderSupportsPatientBillingHistory(billingProvider)
            && this.props.displayContext === DisplayContextEnum.PrimaryDisplay;
    }

    private getCreateClaimReloadFunction(billingProvider: BillingProvider): () => void {
        if (this.shouldUsePatientBillingHistory(billingProvider)) {
            return () => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ExternalBilling_IncrementPatientBillingHistoryVersion);
        }

        const encounterId = this.props.NoteModel.get("Encounter").Id;
        return () => sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.ExternalBilling_ReloadClaims,
            {
                encounterId: encounterId,
                forceStatusUpdate: true // force status update after we're done with the bill
            }
        );
    }
}

export { MedicalNote };
